import * as React10 from 'react';
import { Fragment as Fragment$1 } from 'react';
import { RISK_LEVEL_LABELS, intersectsSelectedSet, inSelectedSet, TableFilterBar, rankedCounts, ChainAvatar, Badge, FinalityPill, RiskBadge, cn, columnIds, LiveModeControl, ResourceTable, useNow, useQuery, notifyError, buildScopeChoices as buildScopeChoices$1, ScopeSwitcher, TagList, MiddleEllipsis, CopyButton, SagaPill, TransactionFlagBadge, TokenValue, AddressDisplay, HashDisplay, AttributionChips, useAddressDisplayName } from '@authe/ui-core';
export { readInitialColumnVisibility, readInitialColumnWidths, usePersistColumnVisibility, usePersistColumnWidths, withPinnedColumnsVisible } from '@authe/ui-core';
import { useTableViews, useTableSavedViews } from '@authe/saved-views';
export { columnPrefsToVisibility, sortRulesToSorting, sortingToSortRules, visibilityToColumnPrefs } from '@authe/saved-views';
import { FinalityState, chainDisplayName, getChainMetadata, coerceFinality, familyFor, toCanonicalChainId, toStoredChainId, resolveFinality, confirmationProgress } from '@authe/fe-commons/chains';
export { coerceFinality } from '@authe/fe-commons/chains';
import { sagaIdLabel, titleRole, sagaLabel } from '@authe/fe-commons/grouping';
import { catalogBundleChips } from '@authe/fe-commons/overlays';
import { formatRelativeTime, formatTimestamp } from '@authe/fe-commons/time';
import { formatUsd, nativeTokenMeta } from '@authe/fe-commons/value';
import { isDebugEnabled } from '@authe/fe-commons/config';
import { claimedRiskLevel, riskPolicyLabel } from '@authe/fe-commons/precedence';
import { QUALITY_STATES, qualityStateOf, summaryFromRow } from '@authe/fe-commons/quality';
import { RowQualityBadge } from '@authe/ui-core/quality';
import { triageFlagsOf, TRIAGE_FLAGS, TRIAGE_LABEL, TRIAGE_MEANING } from '@authe/fe-commons/triage';
import { fieldLabels, TX_FIELDS, fieldLabel, asString, getTruncation, asTrimmedString } from '@authe/fe-commons/display';
import { FALLBACK_NATIVE_PROOF_MIN, NON_PROTOCOL_IDS, nativeProofMinForFamily, deriveDustSenders, withoutDust, pricedAboveDust, dustReason, FALLBACK_PROTOCOL_ID } from '@authe/fe-commons/dust';
export { DEFAULT_CAMPAIGN_MIN_RECIPIENTS, DEFAULT_DUST_USD, pricedAboveDust } from '@authe/fe-commons/dust';
import { isGlobalTenantSegment } from '@authe/fe-commons/tenancy';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { DEFAULT_TX_COLUMNS, ExportMenu } from '@authe/export';
export { DEFAULT_TX_COLUMNS, exportTransactionsCsv } from '@authe/export';
import { transactionFlag, strongestFlag, unsolicitedDistribution } from '@authe/fe-commons/spam';
import { parseTokenImpersonation, counterfeitTokenFlag } from '@authe/fe-commons/tokens';
import { spoofedTransfers, fabricatesSender, deriveImpersonations, poisoningReason, poisoningAddress, paidLookalikeReason, isPoisoningCatalogTag, NATIVE_ASSET } from '@authe/fe-commons/poisoning';
export { DEFAULT_POISONING_POLICY, POISONING_CHECK_ID, POISONING_POLICY_PARAMS, clampPoisoningPolicy, isPoisoningCatalogTag, poisoningAddress, poisoningPolicyFromParams, poisoningPolicyWithinLimits, unvouchedTokens } from '@authe/fe-commons/poisoning';
import { parseEvents, parseProcessingStats, parseDecodeConfig, parsePipelineExecution, parseConfidenceGate, parseGroupView, cdnUrlForKey, isOffloadPlaceholder, rehydrateItem, createEnvelopeBodyLoader, cloneBody, mergeSlice } from '@authe/fe-commons/storage';
import { TX_INDEXED_FILTERS, TX_INDEXED_FILTER_PARAMS, txListNoticesOf, scopeReadQuery, TransactionDetailSchema, transactionScopeDescription, transactionScopeLabel } from '@authe/fe-commons/models';
import { useResourceStream } from '@authe/ui-core/stream';
import { useChainTips, useOptionalStreamingClient } from '@authe/streaming';
import { useDepsChanged, useApiClient, useTransport, useGraphqlQuery } from '@authe/ui-core/api';
import { createLogger } from '@authe/fe-commons/logging';
import { CATALOG_RESOURCES } from '@authe/fe-commons/catalog';
import { readDebugResources, unwrapData } from '@authe/fe-commons/api';
import { useDebugResource } from '@authe/ui-core/debug';
import { toNetFlowRows, toTokenFlowRows, coerceTransactionRows, coerceTransactionRow } from '@authe/fe-commons/transpose';
export { coerceTransactionRow, coerceTransactionRows, toNetFlowRows, toTokenFlowRows, txEntryUniqueAddresses, unwrapTxEnvelope } from '@authe/fe-commons/transpose';
import { fetchLatestTransactions } from '@authe/fe-commons/graphql';

// src/TransactionTable.tsx
function isSystemTxRow(row) {
  return isGlobalTenantSegment(row.org_id) && isGlobalTenantSegment(row.tenant_id);
}
function rowProtocols(row) {
  const primary = typeof row.primary_protocol_id === "string" ? row.primary_protocol_id : void 0;
  const rest = Array.isArray(row.protocol_ids) ? row.protocol_ids.filter((id) => Boolean(id)) : [];
  return [...new Set(primary ? [primary, ...rest] : rest)];
}
function headlineProtocolId(row) {
  return rowProtocols(row).find((id) => !NON_PROTOCOL_IDS.has(id));
}
function closeTo(a, b) {
  return Math.abs(a - b) <= Math.max(1, Math.abs(b) * 0.01);
}
function subjectFlowReplacesTotal(flow, txUsd, direction = null) {
  if (!flow) return false;
  if (flow.sent && flow.received) return true;
  const side = flow.sent ? flow.sentUsd : flow.received ? flow.receivedUsd : null;
  if (side === null) return false;
  if (flow.received && direction === "out" || flow.sent && direction === "in") return true;
  return txUsd === null || !closeTo(side, txUsd);
}
function subjectFlowUsd(flow) {
  const sides = [flow.sent ? flow.sentUsd : null, flow.received ? flow.receivedUsd : null].filter(
    (v) => v !== null
  );
  return sides.length > 0 ? Math.max(...sides) : null;
}
function subjectFlowTitle(flow, txUsd) {
  return [
    "What this row's subject sent and received in the transaction.",
    txUsd !== null ? `The transaction as a whole moved ${formatUsd(txUsd)} across every asset that changed hands, routing hops included.` : null,
    flow.unpriced ? "Some legs are unpriced, so these are lower bounds." : null
  ].filter(Boolean).join(" ");
}
function SubjectFlowCell({ flow, txUsd }) {
  const amount = (usd) => usd === null ? "unpriced" : `${flow.unpriced ? "\u2265" : ""}${formatUsd(usd)}`;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: "inline-flex cursor-default flex-col items-end gap-0.5 text-xs font-semibold tabular-nums",
      title: subjectFlowTitle(flow, txUsd),
      children: [
        flow.sent ? /* @__PURE__ */ jsxs("span", { className: "text-risk-high", children: [
          "\u2212",
          amount(flow.sentUsd)
        ] }) : null,
        flow.received ? /* @__PURE__ */ jsxs("span", { className: "text-finality-finalized", children: [
          "+",
          amount(flow.receivedUsd)
        ] }) : null
      ]
    }
  );
}

// src/priceQuality.ts
function count(quality, key) {
  const v = quality?.[key];
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}
function priceQualityFacts(quality) {
  const record = quality && typeof quality === "object" ? quality : null;
  return {
    seen: count(record, "legs_seen"),
    priced: count(record, "legs_priced"),
    derived: count(record, "legs_derived") ?? 0,
    ageSeconds: count(record, "quote_max_age_seconds")
  };
}
function hasNoValueMovement(quality, { nativeAmount: nativeAmount2, amountHidden, assetsMoved }) {
  if (amountHidden || assetsMoved !== 0 || priceQualityFacts(quality).seen !== 0) return false;
  if (typeof nativeAmount2 !== "string" && typeof nativeAmount2 !== "number") return false;
  if (String(nativeAmount2).trim() === "") return false;
  try {
    return BigInt(nativeAmount2) === 0n;
  } catch {
    return false;
  }
}
function movements(n) {
  return `${n.toLocaleString()} ${n === 1 ? "movement" : "movements"}`;
}
function unpricedValueFace(quality, nativeSymbol, { assetsMoved } = {}) {
  const { seen, priced, derived, ageSeconds } = priceQualityFacts(quality);
  const sentNone = `Sent no ${nativeSymbol}.`;
  const age = ageSeconds != null && ageSeconds > 0 ? ` Oldest quote used: ${Math.round(ageSeconds).toLocaleString()} seconds before the transaction.` : "";
  if (assetsMoved != null && assetsMoved > 0 && (seen === 0 || seen === null)) {
    return {
      face: "Unpriced",
      title: `${sentNone} ${assetsMoved.toLocaleString()} ${assetsMoved === 1 ? "asset" : "assets"} moved (NFTs, or tokens without a quotable amount), and the pricing pass has no price for them, so this transaction's value is unknown rather than zero.`
    };
  }
  if (hasNoValueMovement(quality, { nativeAmount: "0", assetsMoved })) {
    return {
      face: "No value moved",
      title: `${sentNone} The pricing pass read its events and none of them moved an asset \u2014 an approval, or another call that transfers nothing.`
    };
  }
  if (seen === 0) {
    return {
      face: "Unpriced",
      title: `${sentNone} The pricing pass counted no priceable movements, but the decoded asset inventory is unavailable. Whether other assets moved is unknown.`
    };
  }
  if (seen != null && seen > 0 && (priced === null || priced === 0)) {
    const estimated = derived > 0 ? ` ${derived.toLocaleString()} were estimated from trades in the transaction.` : "";
    return {
      face: "Unpriced",
      title: `${sentNone} The pricing pass saw ${movements(seen)} and could quote none of them, so this transaction's value is unknown rather than zero: no price feed covers what moved, at this block.${estimated}${age}`
    };
  }
  if (seen != null && priced != null && priced > 0) {
    return {
      face: "Partly priced",
      title: `${sentNone} ${priced.toLocaleString()} of ${movements(seen)} were quoted, but no total was recorded for the row, so the figure is missing rather than zero.${age}`
    };
  }
  return {
    face: "\u2014",
    title: `${sentNone} The pricing pass kept no record for this row, so whether any tokens moved is unknown.`
  };
}
function otherParty(e) {
  if (!e) return void 0;
  if (e.direction === "in") return e.sender ?? e.counterparty;
  return e.counterparty;
}
function getRowEnrichment(row) {
  const candidate = row.enrichment;
  return candidate && typeof candidate === "object" ? candidate : void 0;
}
function resolveRisk(e) {
  const c = otherParty(e);
  if (!c) return null;
  if (c.riskLevel) return c.riskLevel;
  if (typeof c.riskScore === "number") return claimedRiskLevel(void 0, c.riskScore) ?? null;
  return null;
}
function txRowRisk(row) {
  const r = row;
  const score = typeof r.risk_score === "number" && Number.isFinite(r.risk_score) ? r.risk_score : null;
  if (score === null) return null;
  const text = (value) => typeof value === "string" && value.trim() ? value.trim() : null;
  return {
    score,
    level: claimedRiskLevel(r.risk_bucket, score),
    source: text(r.risk_source)?.toLowerCase() ?? null,
    policyId: text(r.risk_policy_id)
  };
}
function normalizeHashSearch(value) {
  return String(value ?? "").trim().toLowerCase().replace(/^0x/, "");
}
function hexMatches(value, search) {
  const raw2 = String(search ?? "").trim().toLowerCase();
  if (!raw2) return true;
  const hay = normalizeHashSearch(value);
  if (!hay) return false;
  const parts = raw2.split(/…|\.\.\./);
  if (parts.length === 2) {
    const head = parts[0].replace(/^0x/, "");
    const tail = parts[1];
    return hay.startsWith(head) && (tail === "" || hay.endsWith(tail));
  }
  return hay.includes(normalizeHashSearch(raw2));
}
var hashContains = (row, columnId, filterValue) => hexMatches(row.getValue(columnId), filterValue);
hashContains.autoRemove = (value) => !String(value ?? "").trim();
function idMatches(id, needle) {
  const hay = id.toLowerCase();
  const parts = needle.split(/…|\.\.\./);
  if (parts.length === 2) {
    const [head, tail] = parts;
    return hay.startsWith(head) && (tail === "" || hay.endsWith(tail));
  }
  return hay.includes(needle);
}
var idsContain = (row, columnId, filterValue) => {
  const needle = String(filterValue ?? "").trim().toLowerCase();
  if (!needle) return true;
  const raw2 = row.getValue(columnId);
  const ids = Array.isArray(raw2) ? raw2 : [raw2];
  return ids.some((id) => idMatches(String(id ?? ""), needle));
};
idsContain.autoRemove = (value) => !String(value ?? "").trim();
function rowValueUsd(row, enrichment = getRowEnrichment(row)) {
  const total = rowTotalUsd(row, enrichment);
  return enrichment?.subjectFlow && subjectFlowReplacesTotal(enrichment.subjectFlow, total, enrichment.direction ?? null) ? subjectFlowUsd(enrichment.subjectFlow) ?? total : total;
}
function rowTotalUsd(row, enrichment = getRowEnrichment(row)) {
  const usd = enrichment?.valueUsd ?? row.value_usd;
  return typeof usd === "number" && Number.isFinite(usd) ? usd : null;
}
function nativeUnits(row) {
  const decimals = nativeTokenMeta(row.chain_id).decimals;
  if (decimals === void 0) return null;
  const raw2 = safeBigInt(row.total_value_raw);
  const scale = 10n ** BigInt(decimals);
  return Number(raw2 / scale) + Number(raw2 % scale) / Number(scale);
}
var sortByValueWith = (usdOf) => (a, b) => {
  const ua = usdOf(a.original);
  const ub = usdOf(b.original);
  if (ua != null && ub != null) return ua === ub ? 0 : ua < ub ? -1 : 1;
  if (ua != null) return 1;
  if (ub != null) return -1;
  const na = nativeUnits(a.original);
  const nb = nativeUnits(b.original);
  if (na === null || nb === null) return na === nb ? 0 : na === null ? -1 : 1;
  return na === nb ? 0 : na < nb ? -1 : 1;
};
var valueUsdInRangeWith = (usdOf) => {
  const fn = (row, _columnId, filterValue) => {
    const range = filterValue;
    if (!range || range.min == null && range.max == null) return true;
    const usd = usdOf(row.original);
    if (usd == null) return false;
    if (range.min != null && usd < range.min) return false;
    if (range.max != null && usd > range.max) return false;
    return true;
  };
  fn.autoRemove = (value) => {
    const range = value;
    return range == null || range.min == null && range.max == null;
  };
  return fn;
};
function safeBigInt(value) {
  try {
    return BigInt(value || "0");
  } catch {
    return 0n;
  }
}
function UnvaluedNativeCell({ row, assetsMoved }) {
  const symbol = nativeTokenMeta(row.chain_id).symbol ?? "native value";
  const { face, title } = unpricedValueFace(
    row.price_quality,
    symbol,
    { assetsMoved }
  );
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: face === "\u2014" ? "text-muted-foreground" : "text-xs text-muted-foreground",
      title,
      "aria-label": face === "\u2014" ? "No priced value" : void 0,
      children: face
    }
  );
}
function monoColumnWidth(kind, affordancePx = 72) {
  const { head, tail } = getTruncation(kind);
  return Math.round((2 + head + tail + 1) * 8.5) + affordancePx;
}
var EmDash = () => /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2014" });
function txRiskTitle(risk) {
  const score = Math.round(risk.score);
  const lines = [
    risk.level ? `This transaction's own risk: ${RISK_LEVEL_LABELS[risk.level]} (score ${score} of 100).` : `This transaction's own risk score is ${score} of 100, below the threshold for any risk level.`
  ];
  if (risk.policyId) lines.push(`Led by the rule "${riskPolicyLabel(risk.policyId)}".`);
  if (risk.source === "model") lines.push("Scored by the model.");
  else if (risk.source === "rules") lines.push("Scored by the risk rules.");
  return lines.join(" ");
}
var sortByTxRiskScore = (a, b) => {
  const sa = txRowRisk(a.original)?.score ?? -1;
  const sb = txRowRisk(b.original)?.score ?? -1;
  return sa === sb ? 0 : sa < sb ? -1 : 1;
};
function isFieldNotLoaded(value) {
  return value == null;
}
var NotLoaded = ({ field }) => /* @__PURE__ */ jsx(
  "span",
  {
    className: "select-none text-muted-foreground",
    title: `${field} hasn't loaded for this row yet.`,
    "aria-label": `${field} not loaded yet`,
    children: "\xB7\xB7\xB7\xB7\xB7"
  }
);
function counterpartyBundle(e) {
  return catalogBundleChips(e?.counterparty ?? {});
}
function senderBundle(e) {
  return catalogBundleChips(e?.sender ?? {});
}
function bundleIsEmpty(bundle) {
  return ["entities", "labels", "tags", "groups"].every(
    (k) => (bundle[k] ?? []).length === 0
  );
}
function UnresolvedSagaLinks() {
  return /* @__PURE__ */ jsx("span", { className: "text-xs italic text-muted-foreground", title: "The cross-chain group lookup has not answered for this transaction. Whether a counterpart is linked is still unknown.", children: "Links not loaded" });
}
function createTransactionColumns(opts = {}) {
  const {
    explorerLinks = true,
    logoResolver,
    locale,
    onDrilldown,
    getTxHref,
    getAddressHref,
    columns,
    getChainHead,
    getSaga,
    getAction,
    getAssets,
    getAssetMovementCount,
    renderProtocol,
    rowMarkers,
    chipPrefs,
    counterpartyChips = false
  } = opts;
  const getEnrichment = opts.getEnrichment ?? getRowEnrichment;
  const hasEnrichmentReader = Boolean(opts.getEnrichment);
  const shownUsd = (row) => rowValueUsd(row, getEnrichment(row));
  const searchFilter = (row, columnId, filterValue) => {
    if (hexMatches(row.getValue(columnId), filterValue)) return true;
    const r = row.original;
    const e = getEnrichment(row.original);
    for (const address of [e?.from, e?.to, r.from, r.to]) {
      if (typeof address === "string" && address && hexMatches(address, filterValue)) return true;
    }
    const needle = String(filterValue ?? "").trim().toLowerCase();
    const entityNames = [
      e?.entity?.name,
      e?.counterparty?.entity?.name,
      e?.sender?.entity?.name,
      r.primary_entity_id
    ];
    if (entityNames.some((name) => typeof name === "string" && name.toLowerCase().includes(needle))) {
      return true;
    }
    if (!getSaga) return false;
    return (getSaga(row.original)?.ids ?? []).some((id) => idMatches(id.value, needle));
  };
  searchFilter.autoRemove = hashContains.autoRemove;
  const chipNames = (bundle) => [
    ...bundle.labels ?? [],
    ...bundle.tags ?? [],
    ...bundle.groups ?? [],
    ...bundle.entities ?? []
  ].map((item) => item.value ? `${item.name}: ${item.value}` : item.name || item.id).filter(Boolean);
  const chipColumn = (id, kind, read, max) => ({
    id,
    header: fieldLabel(id),
    enableSorting: false,
    // The NAMES behind the chips, so the column has a value to filter on.
    //
    // These cells read the enrichment overlay rather than a row field, which
    // until now meant `row.getValue()` returned undefined and a filter over them
    // was impossible — the four most interesting columns in a triage pass (who
    // is this, what did we tag it) were the four you could not narrow by. One
    // entry per chip, so a row carrying three labels is counted under all three
    // and `intersectsSelectedSet` keeps it for any of them.
    accessorFn: (row) => chipNames(read(getEnrichment(row))),
    getUniqueValues: (row) => chipNames(read(getEnrichment(row))),
    filterFn: intersectsSelectedSet,
    meta: { filter: { kind: "facet" } },
    cell: ({ row }) => /* @__PURE__ */ jsx(
      AttributionChips,
      {
        bundle: read(getEnrichment(row.original)),
        kinds: [kind],
        max,
        popoverTitle: fieldLabel(id),
        userPrefs: chipPrefs,
        empty: /* @__PURE__ */ jsx(EmDash, {})
      }
    )
  });
  const AddressChips = ({
    bundle,
    subject,
    address,
    chain
  }) => {
    const shownAs = useAddressDisplayName()(address, chain);
    if (!counterpartyChips) return null;
    if (bundleIsEmpty(bundle)) return null;
    return /* @__PURE__ */ jsx(AttributionChips, { bundle, popoverTitle: subject, userPrefs: chipPrefs, shownAs });
  };
  const labelColumn = (id, read) => chipColumn(id, "label", (e) => catalogBundleChips({ labels: read(e) }));
  const tagColumn = (id, read) => chipColumn(id, "tag", (e) => catalogBundleChips({ tags: read(e) }));
  const groupColumn = (id, read) => chipColumn(id, "group", (e) => catalogBundleChips({ groups: read(e) }), 2);
  const entityColumn = (id, read) => chipColumn(id, "entity", (e) => catalogBundleChips({ entity: read(e) }));
  const registry = {
    // A placeholder so `select` stays a legal `TxColumnId` for hosts that still
    // name it in a stored column list. Filtered out of the resolved set below:
    // the real checkbox column comes from `ResourceTable` under
    // `enableSelection`, and rendering this stub would draw an empty column.
    select: { id: "select" },
    chain_id: {
      accessorKey: "chain_id",
      header: fieldLabel(TX_FIELDS.chainId),
      filterFn: inSelectedSet,
      // Values are CAIP-2 ids, so the facet has to name them the way the rest of
      // the product does or the reader is picking between `eip155:1` and
      // `eip155:10`.
      meta: { filter: { kind: "facet", label: chainDisplayName } },
      cell: ({ getValue }) => /* @__PURE__ */ jsx(ChainAvatar, { chain: String(getValue() ?? ""), size: "sm", logoResolver })
    },
    tx_hash: {
      accessorKey: "tx_hash",
      header: fieldLabel(TX_FIELDS.txHash),
      enableSorting: false,
      // The only pinned column. Every other one is a matter of what this table is
      // for, but the hash is the row's identity: hide it and a row can't be
      // copied, quoted in a report, drilled into, or told apart from the next one
      // — the table stops being about transactions. Everything else stays the
      // user's call.
      enableHiding: false,
      // Tolerant substring match against the FULL hash accessor — the cell is
      // rendered middle-truncated (cosmetic) but search hits the complete value,
      // normalising whitespace/case/`0x` so a paste of the whole hash (or any
      // fragment of it) matches. Widened to the row's cross-chain ids where the
      // host has them; see `searchFilter`.
      filterFn: searchFilter,
      size: monoColumnWidth("hash"),
      cell: ({ row }) => {
        if (isFieldNotLoaded(row.original.tx_hash)) {
          return /* @__PURE__ */ jsx(NotLoaded, { field: fieldLabel(TX_FIELDS.txHash) });
        }
        const hash = row.original.tx_hash;
        if (!hash) return /* @__PURE__ */ jsx(EmDash, {});
        const display = /* @__PURE__ */ jsx(
          HashDisplay,
          {
            hash,
            chain: row.original.chain_id,
            canonicalTxId: row.original.canonical_tx_id ?? void 0,
            showExplorerIcon: explorerLinks && (Boolean(getTxHref?.(row.original)) || !onDrilldown),
            internalHref: getTxHref?.(row.original),
            onDrilldown
          }
        );
        const system = isSystemTxRow(row.original);
        const marks = rowMarkers?.(row.original);
        if (!system && !marks) return display;
        return /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1.5", children: [
          display,
          system ? /* @__PURE__ */ jsx(
            Badge,
            {
              variant: "outline",
              className: "shrink-0 px-1 py-0 text-3xs font-normal text-muted-foreground",
              title: "Processed by an always-on system pipeline, visible to every tenant",
              children: "System"
            }
          ) : null,
          marks
        ] });
      }
    },
    block_timestamp: {
      accessorKey: "block_timestamp",
      header: fieldLabel(TX_FIELDS.blockTimestamp),
      // The one column where a range is the natural question — "what happened
      // that week" — and the only one whose sort order the reader can trust
      // without knowing how the feed was fetched, since it is what the feed is
      // already ordered by.
      meta: { filter: { kind: "date" } },
      cell: ({ getValue }) => {
        const ts = Number(getValue() ?? 0);
        if (!ts) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx("span", { title: formatTimestamp(ts, { locale }), className: "whitespace-nowrap text-muted-foreground", children: formatRelativeTime(ts, { locale }) });
      }
    },
    from: {
      id: "from",
      header: fieldLabel(TX_FIELDS.from),
      enableSorting: false,
      // The full address, so "show me everything touching this wallet" is a
      // filter rather than a re-run of the case. The cell is middle-truncated
      // for width; `hashContains` matches the whole value and tolerates a
      // pasted `0x1234…cdef`, which is what the reader can actually see to copy.
      accessorFn: (row) => getEnrichment(row)?.from ?? "",
      filterFn: hashContains,
      meta: { filter: { kind: "text", placeholder: "0x\u2026" } },
      size: monoColumnWidth("address"),
      cell: ({ row }) => {
        const e = getEnrichment(row.original);
        const addr = e?.from;
        if (!addr) return "from" in row.original ? /* @__PURE__ */ jsx(EmDash, {}) : /* @__PURE__ */ jsx(NotLoaded, { field: fieldLabel(TX_FIELDS.from) });
        return /* @__PURE__ */ jsxs("span", { className: "flex flex-col items-start gap-1", children: [
          /* @__PURE__ */ jsx(
            AddressDisplay,
            {
              address: addr,
              chain: row.original.chain_id,
              entityLabel: e?.sender?.entity?.name,
              internalHref: getAddressHref?.(addr, row.original.chain_id, row.original),
              onDrilldown
            }
          ),
          /* @__PURE__ */ jsx(AddressChips, { bundle: senderBundle(e), subject: "Sender", address: addr, chain: row.original.chain_id })
        ] });
      }
    },
    to: {
      id: "to",
      header: fieldLabel(TX_FIELDS.to),
      enableSorting: false,
      accessorFn: (row) => getEnrichment(row)?.to ?? "",
      filterFn: hashContains,
      meta: { filter: { kind: "text", placeholder: "0x\u2026" } },
      size: monoColumnWidth("address"),
      cell: ({ row }) => {
        const e = getEnrichment(row.original);
        const addr = e?.to;
        if (!addr) return "to" in row.original ? /* @__PURE__ */ jsx(EmDash, {}) : /* @__PURE__ */ jsx(NotLoaded, { field: fieldLabel(TX_FIELDS.to) });
        return /* @__PURE__ */ jsxs("span", { className: "flex flex-col items-start gap-1", children: [
          /* @__PURE__ */ jsx(
            AddressDisplay,
            {
              address: addr,
              chain: row.original.chain_id,
              entityLabel: e?.counterparty?.entity?.name,
              internalHref: getAddressHref?.(addr, row.original.chain_id, row.original),
              onDrilldown
            }
          ),
          /* @__PURE__ */ jsx(AddressChips, { bundle: counterpartyBundle(e), subject: "Counterparty", address: addr, chain: row.original.chain_id })
        ] });
      }
    },
    value: {
      id: "value",
      accessorFn: (row) => row.total_value_raw ?? "0",
      header: fieldLabel(TX_FIELDS.totalValueRaw),
      sortingFn: sortByValueWith(shownUsd),
      // Sorts and FILTERS on dollars (`rowValueUsd`): base units are not
      // comparable across tokens — a row's 1 USDC and another's 1 wei are the
      // same integer — and "at least 1000000000000000000" is not a question
      // anybody asks. `bounds` is off: the column's own min/max are wei and
      // would describe the wrong quantity in a dollar box.
      filterFn: valueUsdInRangeWith(shownUsd),
      meta: {
        align: "right",
        filter: { kind: "number", title: "Value (USD)", bounds: false }
      },
      cell: ({ row }) => {
        if (isFieldNotLoaded(row.original.total_value_raw)) {
          return /* @__PURE__ */ jsx(NotLoaded, { field: fieldLabel(TX_FIELDS.totalValueRaw) });
        }
        if (rowValueUsd(row.original) == null && !row.original.amount_hidden && safeBigInt(row.original.total_value_raw) === 0n) {
          return /* @__PURE__ */ jsx(UnvaluedNativeCell, { row: row.original, assetsMoved: getAssetMovementCount ? getAssetMovementCount(row.original) : getAssets?.(row.original)?.length });
        }
        const e = getEnrichment(row.original);
        const total = rowTotalUsd(row.original, e);
        if (e?.subjectFlow && subjectFlowReplacesTotal(e.subjectFlow, total, e.direction ?? null)) {
          return /* @__PURE__ */ jsx(SubjectFlowCell, { flow: e.subjectFlow, txUsd: total });
        }
        return /* @__PURE__ */ jsx(
          TokenValue,
          {
            amount: row.original.total_value_raw,
            chain: row.original.chain_id,
            valueUsd: rowValueUsd(row.original),
            priceQuality: row.original.price_quality,
            direction: e?.direction ?? null,
            amountHidden: row.original.amount_hidden
          }
        );
      }
    },
    decoding_quality: {
      id: "decoding_quality",
      header: "Decoding quality",
      // Sorts weakest first: the rows worth opening lead.
      accessorFn: (row) => QUALITY_STATES.indexOf(qualityStateOf(summaryFromRow(row))),
      cell: ({ row }) => /* @__PURE__ */ jsx(RowQualityBadge, { row: row.original }),
      size: 130
    },
    finality: {
      id: "finality",
      // Read straight off the row, so the pill, the sort order and the faceted
      // counts are one answer rather than three. Promoting the persisted state
      // with the live head is `useLiveFinalityRows`' job, on the DATA — an
      // accessor that did it here would be computed once per row and cached, and
      // the facet panel would go on offering "Observed" for rows the cells had
      // already promoted (see that hook's note on TanStack's value cache).
      accessorFn: (row) => coerceFinality(row.finality) ?? FinalityState.UNKNOWN,
      header: fieldLabel(TX_FIELDS.finality),
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row, getValue }) => {
        if (isFieldNotLoaded(row.original.finality)) {
          return /* @__PURE__ */ jsx(NotLoaded, { field: fieldLabel(TX_FIELDS.finality) });
        }
        const state = getValue();
        const chainId = String(row.original.chain_id ?? "").toLowerCase();
        const confirmations = confirmationProgress(state, {
          blockNumber: row.original.block_number ?? null,
          head: getChainHead?.(chainId) ?? null,
          chainOrId: chainId
        });
        return /* @__PURE__ */ jsx(FinalityPill, { state, confirmations: confirmations ?? void 0 });
      }
    },
    type: {
      accessorKey: "type",
      header: fieldLabel(TX_FIELDS.type),
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row, getValue }) => {
        if (isFieldNotLoaded(row.original.type)) return /* @__PURE__ */ jsx(NotLoaded, { field: fieldLabel(TX_FIELDS.type) });
        const isContract = getEnrichment(row.original)?.contractInteraction;
        const label = String(getValue() ?? "transaction");
        return isContract ? /* @__PURE__ */ jsx(
          Badge,
          {
            variant: "outline",
            className: "border-risk-medium/40 bg-risk-medium/10 font-normal capitalize text-risk-medium",
            title: "Contract interaction",
            children: label
          }
        ) : /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "font-normal capitalize", children: label });
      }
    },
    // --- the transaction's own attribution -----------------------------------
    // What the table's bulk bar and the inspector write via `PATCH
    // /transactions/{id}`. Tenant-wide and chain-agnostic: a transaction's
    // overlays carry no per-user visibility, so these chips have no scope dot.
    tx_labels: labelColumn("tx_labels", (e) => e?.labels),
    tx_tags: tagColumn("tx_tags", (e) => e?.tags),
    tx_groups: groupColumn("tx_groups", (e) => e?.groups),
    tx_entity: entityColumn("tx_entity", (e) => e?.entity),
    // --- the counterparty address's attribution ------------------------------
    // A DIFFERENT claim about a DIFFERENT thing: written per `(chain, address)`
    // through the assignments API, by someone saying "this wallet is Binance",
    // not "this transfer is suspicious". Describes the row's `to` end (or `from`
    // on a one-ended row) — see the host's enrichment reader for which.
    //
    // Through `otherParty`: on a feed with a subject, "counterparty" is the end
    // that is NOT the subject, which on an incoming row is the sender. The `to`
    // cell still draws `counterparty` under its own address; these columns ask
    // who is on the other side, and must not answer "the wallet you are
    // investigating".
    counterparty_labels: labelColumn("counterparty_labels", (e) => otherParty(e)?.labels),
    counterparty_tags: tagColumn("counterparty_tags", (e) => otherParty(e)?.tags),
    counterparty_groups: groupColumn("counterparty_groups", (e) => otherParty(e)?.groups),
    counterparty_entity: entityColumn("counterparty_entity", (e) => otherParty(e)?.entity),
    // --- the sending address's attribution -----------------------------------
    // The same claim about the row's other end. Off by default like the
    // counterparty set and for the same reason: the chips are already under the
    // address in the `from` cell, and a column repeats them further right under
    // a header rather than under their subject. They exist for the case that
    // wants to FILTER or scan one kind on its own — which is the whole point,
    // since a facet needs a column to hang off.
    sender_labels: labelColumn("sender_labels", (e) => e?.sender?.labels),
    sender_tags: tagColumn("sender_tags", (e) => e?.sender?.tags),
    sender_groups: groupColumn("sender_groups", (e) => e?.sender?.groups),
    sender_entity: entityColumn("sender_entity", (e) => e?.sender?.entity),
    /**
     * The TRANSACTION's own risk — what the pipeline's risk step scored this row
     * — as opposed to `counterparty_risk` below, which is what the catalog knows
     * about the address on the other end. Two subjects, two columns, two labels.
     *
     * Read off the ROW (`txRowRisk`), never the overlay. A score under the
     * lowest band is drawn as an em dash with the score in its title, never as
     * "Safe": nothing a rule found reached `low`, which is not a clearance. An
     * unscored row (one the pipeline never assessed, or an older row) draws
     * nothing at all — there is no verdict to deny.
     */
    tx_risk: {
      id: "tx_risk",
      header: fieldLabel("tx_risk"),
      // Faceted by the rung the verdict claims. Unscored and sub-band rows
      // carry "", which the facet drops rather than offering a nameless option.
      // Sorted by the SCORE (`sortByTxRiskScore`), worst first on the first click.
      accessorFn: (row) => txRowRisk(row)?.level ?? "",
      sortingFn: sortByTxRiskScore,
      sortDescFirst: true,
      filterFn: inSelectedSet,
      meta: {
        filter: {
          kind: "facet",
          label: (level) => RISK_LEVEL_LABELS[level] ?? level
        }
      },
      cell: ({ row }) => {
        const risk = txRowRisk(row.original);
        if (!risk) return null;
        const title = txRiskTitle(risk);
        if (!risk.level) {
          return /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", title, "aria-label": "Risk below threshold", children: "\u2014" });
        }
        return /* @__PURE__ */ jsx("span", { className: "inline-flex", title, children: /* @__PURE__ */ jsx(RiskBadge, { level: risk.level, score: risk.score }) });
      }
    },
    counterparty_risk: {
      id: "counterparty_risk",
      header: fieldLabel("counterparty_risk"),
      enableSorting: false,
      // "Show me only the risky counterparties" is the whole reason this column
      // is ever turned on, and until it had a value it could be read but not
      // narrowed by. Rows with no resolved risk carry `""`, which the facet
      // drops rather than offering as a nameless option.
      accessorFn: (row) => resolveRisk(getEnrichment(row)) ?? "",
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row }) => {
        const e = getEnrichment(row.original);
        const level = resolveRisk(e);
        return level ? /* @__PURE__ */ jsx(RiskBadge, { level, score: otherParty(e)?.riskScore }) : /* @__PURE__ */ jsx(EmDash, {});
      }
    },
    /**
     * What the row IS — poisoning, dust — with the reason one hover away.
     *
     * The answer to "which of these transactions are spam", which this table had
     * no column for: the address columns say what is known about a WALLET, and a
     * poisoning transfer's sender is often a wallet nobody has tagged. The flag
     * is the row's own classification, computed by the host over the history it
     * holds (`classifyTxRows`), so it is present exactly where the evidence is.
     *
     * Faceted like the risk column, because "show me only the poisoning" is the
     * question the column exists for.
     */
    row_flags: {
      id: "row_flags",
      header: fieldLabel("row_flags"),
      enableSorting: false,
      accessorFn: (row) => getEnrichment(row)?.flags?.[0]?.kind ?? "",
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row }) => {
        const flags = getEnrichment(row.original)?.flags ?? [];
        if (flags.length === 0) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx("span", { className: "flex flex-wrap gap-1", children: flags.map((flag) => /* @__PURE__ */ jsx(TransactionFlagBadge, { flag }, flag.kind)) });
      }
    },
    /**
     * The caller's own analyst note.
     *
     * The one column here that carries no chain fact — everything else is
     * something the network said, and this is something a person did. It has
     * been writable for a long time and readable only by opening the
     * transaction, which made it a place notes went to be forgotten: the whole
     * value of "I checked this counterparty myself" is that the next person
     * scanning the table sees it without opening anything.
     *
     * Truncated to one line with the full text on hover, because a note is prose
     * of arbitrary length in a row of fixed height, and a column that grows with
     * its longest note makes every other row unreadable.
     *
     * Off by default, like the other opt-in columns: most feeds have no notes,
     * and a permanently empty column is a permanent cost.
     */
    notes: {
      id: "notes",
      // The header carries the scope, because the column cannot. A note here is
      // about THIS transaction; a fact about the counterparty belongs on the
      // address, where one edit covers every row that touches it instead of
      // forty rows disagreeing with each other.
      header: () => /* @__PURE__ */ jsx("span", { title: "Your note on this transaction. A fact about an address belongs on the address.", children: fieldLabel("notes") }),
      enableSorting: false,
      accessorFn: (row) => {
        const note = row.notes;
        return typeof note === "string" ? note : "";
      },
      meta: { filter: { kind: "text", placeholder: "Search notes\u2026" } },
      cell: ({ row }) => {
        const note = row.original.notes;
        const text = typeof note === "string" ? note.trim() : "";
        if (!text) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx("span", { className: "block max-w-[22rem] truncate text-xs", title: text, children: text });
      }
    },
    /**
     * The caller's own JUDGEMENT on this transaction — disputed, archived.
     *
     * Both surfaces that SET triage (the transaction workspace and the graph
     * inspector) have always had it; no surface that LISTS transactions did. So
     * a reader could mark forty rows disputed and then find that decision in no
     * column, no badge and no filter among the twenty-eight this table offers —
     * the only way to read one back was to reopen the transaction that carried
     * it, one at a time. A judgement you can write and cannot review is worse
     * than one you cannot make.
     *
     * Read through the shared `triageFlagsOf`, which also understands the legacy
     * `caller_status` string — a row projection predating the split would
     * otherwise report a disputed transaction as untriaged.
     *
     * Off by default, like `notes`: most feeds carry no triage at all.
     */
    triage: {
      id: "triage",
      header: fieldLabel("triage"),
      enableSorting: false,
      // One value per row so the facet can narrow by it, and the same order the
      // two editing surfaces offer. "" for an untriaged row, which the facet
      // drops — untriaged is the absence of a judgement, not a third verdict.
      accessorFn: (row) => TRIAGE_FLAGS.filter((flag) => triageFlagsOf(row)[flag]).join(" "),
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row }) => {
        const flags = triageFlagsOf(row.original);
        const set = TRIAGE_FLAGS.filter((flag) => flags[flag]);
        if (set.length === 0) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx("span", { className: "flex flex-wrap gap-1", children: set.map((flag) => /* @__PURE__ */ jsx(
          Badge,
          {
            variant: "outline",
            title: TRIAGE_MEANING[flag],
            className: flag === "disputed" ? "border-warning/60 font-normal text-warning" : "border-muted-foreground/30 bg-muted font-normal text-muted-foreground",
            children: TRIAGE_LABEL[flag]
          },
          flag
        )) });
      }
    },
    // Distinct from `groups` above, which shows the CURATED catalog collections a
    // row was filed into. This is the machine cross-chain saga: the legs a
    // protocol's own correlation id ties together, and whether they all arrived.
    saga: {
      id: "saga",
      header: fieldLabel("saga"),
      enableSorting: false,
      // The one question a saga column exists to answer — "did it land?" — as a
      // value the toolbar can narrow by. A row that is not a bridge leg has no
      // saga at all and carries `""`, which the facet drops: "not part of a
      // saga" is the absence of the subject, not a third outcome alongside
      // complete and incomplete.
      accessorFn: (row) => {
        const saga = getSaga?.(row);
        if (!saga || saga.total === 0) return "";
        if (saga.complete === saga.total) return "complete";
        return saga.closed === saga.total ? "closed without delivery" : "incomplete";
      },
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row }) => {
        const saga = getSaga?.(row.original);
        if (!saga || saga.total === 0) {
          if (saga?.linksPending) return /* @__PURE__ */ jsx(UnresolvedSagaLinks, {});
          if (saga?.bridgedVia != null) {
            const via = saga.bridgedVia ? ` via ${saga.bridgedVia}` : "";
            return /* @__PURE__ */ jsxs(
              "span",
              {
                className: "text-xs text-muted-foreground",
                title: `Bridge protocol activity${via}, but no verified counterpart is linked. Protocol attribution alone does not prove that value crossed chains. Inspect the decoded transfer and correlation evidence.`,
                children: [
                  "Bridge activity",
                  via
                ]
              }
            );
          }
          return /* @__PURE__ */ jsx(EmDash, {});
        }
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
          /* @__PURE__ */ jsx(
            SagaPill,
            {
              label: sagaLabel(saga.sagaKind),
              missing: saga.missingRoles.map(titleRole),
              complete: saga.complete,
              closed: saga.closed,
              total: saga.total,
              confirmed: saga.confirmed ?? false
            }
          ),
          saga.role ? /* @__PURE__ */ jsx("span", { className: "text-3xs capitalize text-muted-foreground", children: saga.role }) : null
        ] });
      }
    },
    // The correlation id itself, where `saga` above renders whether every leg
    // arrived. This is the value that goes into a report, or into the protocol's
    // own explorer, and it's what makes two rows on different chains provably the
    // same message. Both columns read the same derived sagas, so a host already
    // showing one pays nothing to show the other.
    cross_chain_id: {
      id: "cross_chain_id",
      header: fieldLabel("cross_chain_id"),
      enableSorting: false,
      // The COMPLETE ids: the cell is middle-truncated for width, but a pasted
      // message id has to find its row.
      accessorFn: (row) => (getSaga?.(row)?.ids ?? []).map((id) => id.value),
      filterFn: idsContain,
      meta: { filter: { kind: "text", placeholder: "Paste a message id\u2026" } },
      size: monoColumnWidth("hash"),
      cell: ({ row }) => {
        const saga = getSaga?.(row.original);
        const ids = saga?.ids ?? [];
        if (ids.length === 0) {
          if (saga?.linksPending) return /* @__PURE__ */ jsx(UnresolvedSagaLinks, {});
          if (saga?.pending) {
            return /* @__PURE__ */ jsx("span", { className: "text-xs italic text-muted-foreground", title: "This row's decoded body hasn't been loaded, so its cross-chain ids are unknown.", children: "Body not loaded" });
          }
          if (saga?.bridgedVia != null) {
            const via = saga.bridgedVia ? ` via ${saga.bridgedVia}` : "";
            return /* @__PURE__ */ jsxs(
              "span",
              {
                className: "text-xs text-muted-foreground",
                title: `Bridge protocol activity${via}, but no correlation id is available in this record. This may reflect incomplete decoding or unresolved counterpart lookup; it does not establish that the protocol publishes no ids or that the transfer completed.`,
                children: [
                  "Bridge activity",
                  via,
                  " \xB7 no linked id"
                ]
              }
            );
          }
          return /* @__PURE__ */ jsx(EmDash, {});
        }
        return /* @__PURE__ */ jsx(
          TagList,
          {
            popoverTitle: "Cross-chain ids",
            max: 1,
            items: ids.map((id) => /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsx(
                MiddleEllipsis,
                {
                  value: id.value,
                  kind: "hash",
                  title: `${sagaIdLabel(id.label)}: ${id.value}`,
                  className: "text-xs"
                }
              ),
              /* @__PURE__ */ jsx(CopyButton, { value: id.value, label: `Copy ${sagaIdLabel(id.label)}` })
            ] }, id.value))
          }
        );
      }
    },
    primary_entity_id: {
      accessorKey: "primary_entity_id",
      header: fieldLabel(TX_FIELDS.primaryEntityId),
      meta: { filter: { kind: "text" } },
      cell: ({ getValue }) => {
        const id = getValue();
        return id ? /* @__PURE__ */ jsx("span", { className: "text-sm", children: id }) : /* @__PURE__ */ jsx(EmDash, {});
      }
    },
    // What the row DID, as the decode read it. The `type` column two cells left
    // says "Transaction" for every contract call in the feed — true, and an
    // answer to nothing. This is the sentence the transaction's own page leads
    // with, in the column where a reader scans for it.
    tx_action: {
      id: "tx_action",
      header: fieldLabel("tx_action"),
      enableSorting: false,
      accessorFn: (row) => getAction?.(row)?.label ?? "",
      getUniqueValues: (row) => {
        const label = getAction?.(row)?.label;
        return label ? [label] : [];
      },
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row }) => {
        const action = getAction?.(row.original);
        if (action?.pending) {
          return /* @__PURE__ */ jsx("span", { className: "text-xs italic text-muted-foreground", title: "This row's decoded body hasn't been loaded, so what it did is unknown.", children: "Body not loaded" });
        }
        if (!action?.label) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "font-normal", children: action.label });
      }
    },
    // WHAT moved, as opposed to how much it was worth. The `value` column is the
    // chain's native amount and its USD: an NFT purchase reads there as the ETH
    // that left, and the token it bought appears nowhere. A case following a
    // wallet that mints NFTs could not name a single one of them.
    assets: {
      id: "assets",
      header: fieldLabel("assets"),
      enableSorting: false,
      accessorFn: (row) => (getAssets?.(row) ?? []).map((a) => a.text),
      filterFn: idsContain,
      meta: { filter: { kind: "text", placeholder: "Token, symbol or id\u2026" } },
      cell: ({ row }) => {
        const assets = getAssets?.(row.original) ?? [];
        if (assets.length === 0) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx(
          TagList,
          {
            popoverTitle: "Assets moved",
            max: 2,
            items: assets.map((asset, i) => /* @__PURE__ */ jsxs(
              "span",
              {
                className: [
                  "text-xs tabular-nums",
                  asset.flagged ? "text-muted-foreground" : "",
                  !asset.flagged && asset.direction === "in" ? "text-finality-finalized" : "",
                  !asset.flagged && asset.direction === "out" ? "text-risk-high" : ""
                ].filter(Boolean).join(" "),
                title: asset.flagged ? `${asset.title ?? asset.text} \u2014 ${asset.flagged}` : asset.title ?? asset.text,
                children: [
                  asset.text,
                  asset.flagged ? /* @__PURE__ */ jsx("span", { className: "ml-1 text-3xs uppercase tracking-wide", children: "flagged" }) : null
                ]
              },
              `${asset.text}-${i}`
            ))
          }
        );
      }
    },
    protocol: {
      id: "protocol",
      // Faceting counts each protocol separately rather than treating a row's
      // whole set as one value, so the toolbar filter can offer "chainlink_ccip
      // (12)" over rows that also carry three other protocols.
      accessorFn: (row) => rowProtocols(row),
      getUniqueValues: (row) => rowProtocols(row),
      header: fieldLabel("protocol"),
      enableSorting: false,
      filterFn: intersectsSelectedSet,
      // The facet's VALUES come from the page (see `getUniqueValues` above); its
      // display names do not — only the brand registry turns `chainlink_ccip`
      // into "Chainlink CCIP", and that lives in the host. It arrives as a
      // filter override.
      meta: { filter: { kind: "facet" } },
      cell: ({ row }) => {
        const protocols = rowProtocols(row.original);
        if (protocols.length === 0) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx(
          TagList,
          {
            popoverTitle: "Protocols",
            max: 2,
            items: protocols.map((id) => (
              // The neutral fallback shows the raw id rather than a humanized
              // one: `erc20` / `chainlink_ccip` are what the API returns and what
              // an investigator cross-references, and Title Case would render
              // them as "Erc20". Hosts that want display names inject a renderer
              // backed by the brand registry, which actually knows them.
              /* @__PURE__ */ jsx(Fragment$1, { children: renderProtocol?.(id, row.original) ?? /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "font-normal", children: id }) }, id)
            ))
          }
        );
      }
    }
  };
  const defaultColumns = hasEnrichmentReader ? [
    "chain_id",
    "tx_hash",
    "from",
    "to",
    "value",
    "counterparty_risk",
    "counterparty_labels",
    "counterparty_tags",
    "type",
    "finality"
  ] : ["chain_id", "tx_hash", "block_timestamp", "value", "finality", "type", "primary_entity_id"];
  const chosen = columns ?? defaultColumns;
  const selected = (opts.debugColumns ?? isDebugEnabled()) && !chosen.includes("decoding_quality") ? [...chosen, "decoding_quality"] : chosen;
  return selected.filter((id) => id !== "select" && registry[id]).map((id) => registry[id]);
}
var TX_COLUMN_LABELS = fieldLabels(
  [
    TX_FIELDS.chainId,
    TX_FIELDS.txHash,
    TX_FIELDS.blockTimestamp,
    TX_FIELDS.from,
    TX_FIELDS.to,
    TX_FIELDS.finality,
    TX_FIELDS.type,
    TX_FIELDS.primaryEntityId
  ],
  {
    value: fieldLabel(TX_FIELDS.totalValueRaw),
    saga: fieldLabel("saga"),
    cross_chain_id: fieldLabel("cross_chain_id"),
    protocol: fieldLabel("protocol"),
    tx_action: fieldLabel("tx_action"),
    assets: fieldLabel("assets"),
    // Both attribution subjects, spelled out: this map IS the column menu, and
    // the menu is the only place a user learns that the table can tell them apart.
    tx_labels: fieldLabel("tx_labels"),
    tx_tags: fieldLabel("tx_tags"),
    tx_groups: fieldLabel("tx_groups"),
    tx_entity: fieldLabel("tx_entity"),
    // The transaction's own risk and its counterparty's, named apart for the
    // same reason as the attribution pair.
    tx_risk: fieldLabel("tx_risk"),
    counterparty_labels: fieldLabel("counterparty_labels"),
    counterparty_tags: fieldLabel("counterparty_tags"),
    counterparty_groups: fieldLabel("counterparty_groups"),
    counterparty_entity: fieldLabel("counterparty_entity"),
    counterparty_risk: fieldLabel("counterparty_risk"),
    row_flags: fieldLabel("row_flags"),
    sender_labels: fieldLabel("sender_labels"),
    sender_tags: fieldLabel("sender_tags"),
    sender_groups: fieldLabel("sender_groups"),
    sender_entity: fieldLabel("sender_entity"),
    // In the menu but not in any default set: most feeds have no notes, and a
    // permanently empty column is a permanent cost. The menu IS how somebody
    // learns the table can show one.
    notes: fieldLabel("notes"),
    triage: fieldLabel("triage")
  }
);
var DEFAULT_TX_FACETS = ["chain_id", "finality", "type"];
function transactionFilterOverrides(options, labels) {
  const overrides = {};
  for (const [field, value] of Object.entries(options ?? {})) {
    if (value) overrides[field] = { ...overrides[field], options: value };
  }
  for (const [field, value] of Object.entries(labels ?? {})) {
    if (value) overrides[field] = { ...overrides[field], label: value };
  }
  return overrides;
}
function TransactionFacets({
  table,
  facets = DEFAULT_TX_FACETS,
  options,
  labels
}) {
  return /* @__PURE__ */ jsx(
    TableFilterBar,
    {
      table,
      columns: facets,
      overrides: transactionFilterOverrides(options, labels)
    }
  );
}
function summarizeTransactions(rows, getEnrichment = getRowEnrichment) {
  const list2 = Array.isArray(rows) ? rows : [];
  const chains = /* @__PURE__ */ new Map();
  const finality = /* @__PURE__ */ new Map();
  const types = /* @__PURE__ */ new Map();
  const risk = /* @__PURE__ */ new Map();
  let totalValueUsd = null;
  let contractInteractions = 0;
  const bump = (map, key) => {
    if (key == null || key === "") return;
    map.set(key, (map.get(key) ?? 0) + 1);
  };
  for (const row of list2) {
    bump(chains, row.chain_id);
    bump(finality, row.finality ? String(row.finality) : null);
    bump(types, row.type ? String(row.type) : null);
    const e = getEnrichment(row);
    const level = resolveRisk(e);
    if (level) risk.set(level, (risk.get(level) ?? 0) + 1);
    if (e?.valueUsd != null) totalValueUsd = (totalValueUsd ?? 0) + e.valueUsd;
    if (e?.contractInteraction) contractInteractions += 1;
  }
  return {
    total: list2.length,
    chains: rankedCounts(chains),
    finality: rankedCounts(finality),
    types: rankedCounts(types),
    risk: [...risk.entries()].map(([level, count3]) => ({ level, count: count3 })),
    totalValueUsd,
    contractInteractions
  };
}
function Stat({ label, children }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
    /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: label }),
    children
  ] });
}
function TransactionLegend({
  rows,
  getEnrichment,
  maxChains = 6,
  className
}) {
  const summary = React10.useMemo(() => summarizeTransactions(rows, getEnrichment), [rows, getEnrichment]);
  const shownChains = summary.chains.slice(0, maxChains);
  const extraChains = summary.chains.length - shownChains.length;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "scroll-fade-x flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border bg-card/40 px-3 py-2 text-sm",
        className
      ),
      children: [
        /* @__PURE__ */ jsx(Stat, { label: "Transactions", children: /* @__PURE__ */ jsx("span", { className: "font-semibold tabular-nums", children: summary.total.toLocaleString() }) }),
        summary.totalValueUsd != null && /* @__PURE__ */ jsx(Stat, { label: "Total", children: /* @__PURE__ */ jsx("span", { className: "font-semibold tabular-nums", children: formatUsd(summary.totalValueUsd) }) }),
        shownChains.length > 0 && /* @__PURE__ */ jsx(Stat, { label: fieldLabel("chain_id"), children: /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
          shownChains.map((c) => /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
            /* @__PURE__ */ jsx(ChainAvatar, { chain: c.key, size: "sm" }),
            /* @__PURE__ */ jsx("span", { className: "text-xs tabular-nums text-muted-foreground", children: c.count })
          ] }, c.key)),
          extraChains > 0 && /* @__PURE__ */ jsxs(
            Badge,
            {
              variant: "outline",
              className: "text-muted-foreground",
              title: summary.chains.slice(maxChains).map((c) => `${getChainMetadata(c.key)?.name ?? c.key} ${c.count}`).join(", "),
              children: [
                "+",
                extraChains
              ]
            }
          )
        ] }) }),
        summary.finality.length > 0 && /* @__PURE__ */ jsx(Stat, { label: fieldLabel("finality"), children: /* @__PURE__ */ jsx("span", { className: "flex flex-wrap items-center gap-1", children: summary.finality.map((f) => {
          const state = coerceFinality(f.key);
          return state ? /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
            /* @__PURE__ */ jsx(FinalityPill, { state }),
            /* @__PURE__ */ jsx("span", { className: "text-xs tabular-nums text-muted-foreground", children: f.count })
          ] }, f.key) : /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-muted-foreground", children: [
            f.key,
            " ",
            f.count
          ] }, f.key);
        }) }) }),
        summary.risk.length > 0 && /* @__PURE__ */ jsx(Stat, { label: fieldLabel("counterparty_risk"), children: /* @__PURE__ */ jsx("span", { className: "flex flex-wrap items-center gap-1", children: summary.risk.map((r) => /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
          /* @__PURE__ */ jsx(RiskBadge, { level: r.level }),
          /* @__PURE__ */ jsx("span", { className: "text-xs tabular-nums text-muted-foreground", children: r.count })
        ] }, r.level)) }) }),
        summary.contractInteractions > 0 && /* @__PURE__ */ jsx(Stat, { label: "Contracts", children: /* @__PURE__ */ jsx("span", { className: "font-medium tabular-nums", children: summary.contractInteractions }) })
      ]
    }
  );
}

// src/rowId.ts
function identifying(value) {
  return typeof value === "string" && value !== "" ? value : void 0;
}
function txRowId(row) {
  return identifying(row.canonical_tx_id) ?? identifying(row.tx_hash);
}
function txStreamKey(row) {
  const id = txRowId(row);
  if (id == null) return null;
  const log5 = row.log_index;
  return log5 == null ? id : `${id}:${String(log5)}`;
}
function txSelectionRowId(row, index) {
  const key = txStreamKey(row);
  if (key != null) return key;
  return index == null ? void 0 : `idx:${index}`;
}
function rowBlockTimeMs(row) {
  const raw2 = row.block_timestamp;
  let value;
  if (typeof raw2 === "number") value = raw2;
  else if (typeof raw2 === "string" && raw2.trim() !== "") {
    const n = Number(raw2);
    value = Number.isFinite(n) ? n : Date.parse(raw2) / 1e3;
  } else return null;
  if (!Number.isFinite(value) || value <= 0) return null;
  return value < 1e12 ? value * 1e3 : value;
}
function rowChainId(row) {
  return identifying(row.chain_id) ?? null;
}

// src/addressPoisoning.ts
var NATIVE_PROVEN_MIN_RAW = FALLBACK_NATIVE_PROOF_MIN;
function nativeProofMin(chainId) {
  const family = typeof chainId === "string" && chainId ? familyFor(chainId) : null;
  return nativeProofMinForFamily(family);
}
function nativeValueProves(chainId, value) {
  return amountProves(nativeProofMin(chainId), value);
}
function amountProves(floor, value) {
  return floor !== null && (raw(value) ?? 0n) >= floor;
}
function legCandidate(rowId, from, to, protocol, provenValue, facts = {}) {
  return {
    rowId,
    canonicalTxId: rowId,
    fromAddress: from,
    toAddress: to,
    valueUsd: null,
    primaryProtocolId: protocol,
    protocolIds: [protocol],
    provenValue,
    ...facts
  };
}
function fold(value, chainId) {
  if (typeof value !== "string" || !value) return null;
  return poisoningAddress(value, typeof chainId === "string" && chainId ? chainId : null);
}
function foldedPoisoners(poisoners) {
  if (!poisoners || poisoners.size === 0) return void 0;
  const out = /* @__PURE__ */ new Set();
  for (const address of poisoners) {
    const key = fold(address);
    if (key) out.add(key);
  }
  return out.size > 0 ? out : void 0;
}
function rowTimestamp(row) {
  const ms = rowBlockTimeMs(row);
  return ms === null ? null : Math.floor(ms / 1e3);
}
function amountText(value) {
  const amount = raw(value);
  return amount === null ? null : amount.toString();
}
function raw(value) {
  if (typeof value === "number" && Number.isFinite(value)) return BigInt(Math.trunc(value));
  if (typeof value !== "string" || !/^(0x[0-9a-f]+|\d+)$/i.test(value.trim())) return null;
  try {
    return BigInt(value.trim());
  } catch {
    return null;
  }
}
function list(value) {
  return Array.isArray(value) ? value.filter((v) => Boolean(v) && typeof v === "object") : [];
}
function tokenOf(leg) {
  const token = leg.token_address ?? leg.contract_address ?? leg.address;
  return typeof token === "string" && token ? token : null;
}
function legsOf(row, subject) {
  const rowId = txRowId(row);
  if (!rowId) return [];
  const r = row;
  const legs = [];
  const timestamp = rowTimestamp(row);
  const add = (from, to, protocol, provenValue, facts) => {
    const f = fold(from, r.chain_id);
    const t = fold(to, r.chain_id);
    if (!f || !t || f !== subject && t !== subject) return;
    legs.push(legCandidate(rowId, f, t, protocol, provenValue, { timestamp, ...facts }));
  };
  for (const leg of list(r.erc20_transfers)) {
    add(leg.from_address, leg.to_address, "erc20", false, {
      token: tokenOf(leg),
      amountRaw: amountText(leg.value)
    });
  }
  for (const leg of list(r.nft_transfers)) add(leg.from_address, leg.to_address, "erc721", false, { token: tokenOf(leg) });
  const native = list(r.native_transfers);
  const floor = nativeProofMin(r.chain_id);
  const proves = (value2) => amountProves(floor, value2);
  for (const leg of native) {
    add(leg.from_address, leg.to_address, "native_transfer", proves(leg.value), {
      token: NATIVE_ASSET,
      amountRaw: amountText(leg.value)
    });
  }
  const value = native.length === 0 ? r.value ?? r.total_value_raw : "0";
  add(r.from ?? r.from_address, r.to ?? r.to_address, "native_transfer", proves(value), {
    token: NATIVE_ASSET,
    amountRaw: amountText(value)
  });
  return legs;
}
function rowSpoofable(row) {
  const r = row;
  const fromLogs = transfersFromEvents(parseEvents(row));
  if (fromLogs.length > 0) return fromLogs;
  const out = [];
  const push = (kind, leg) => out.push({
    kind,
    from: fold(leg.from_address),
    to: fold(leg.to_address),
    value: typeof leg.value === "string" ? leg.value : null,
    token: fold(tokenOf(leg))
  });
  for (const leg of list(r.erc20_transfers)) push("erc20", leg);
  for (const leg of list(r.nft_transfers)) push("nft", leg);
  return out;
}
var count2 = (value) => value.toLocaleString("en-US");
function addressFeedStanding(rows, subject, options = {}) {
  const { legsOf: legsOf2, isContract = false } = options;
  const wallet = fold(subject);
  if (!wallet) return null;
  const legs = (row) => {
    const read = legsOf2?.(row);
    return read === void 0 ? rowSpoofable(row) : [...read];
  };
  const evidence = (row) => ({
    impersonations: options.impersonations,
    catalogued: options.catalogued,
    unvouchedTokens: options.unvouchedTokensOf?.(row) ?? options.unvouchedTokens
  });
  let signed = 0;
  let spoofed = 0;
  const written = /* @__PURE__ */ new Set();
  const signers = /* @__PURE__ */ new Set();
  for (const row of rows) {
    const r = row;
    const transfers = legs(row);
    if (transfers.length === 0) continue;
    const signer = fold(r.from ?? r.from_address);
    const report = spoofedTransfers({ ...evidence(row), signer, called: fold(r.to ?? r.to_address), transfers });
    if (!report) continue;
    if (signer === wallet) {
      signed += 1;
      for (const leg of report.legs) if (leg.to) written.add(leg.to);
    } else if (report.legs.some((leg) => fold(leg.from) === wallet)) {
      spoofed += 1;
      if (signer) signers.add(signer);
    }
  }
  if (signed > 0) {
    return {
      role: "sender",
      transactions: signed,
      wallets: written.size,
      // Counts are formatted: this one runs over a campaign's own rows, where
      // "writing entries into 31626 other histories" is a number nobody reads.
      // WHERE they were counted is the caller's sentence, not this one's — the
      // rows can be a page of the feed or the transactions a workspace ingested.
      reason: `${count2(signed)} transaction${signed === 1 ? "" : "s"} fabricate token transfers out of wallets that never signed them, writing entries into ${count2(written.size)} other histor${written.size === 1 ? "y" : "ies"} \u2014 the shape of an address-poisoning campaign. Those entries are not payments anyone made.`
    };
  }
  if (isContract) return null;
  if (spoofed === 0 && signed === 0 && rows.length > 0) {
    const named = /* @__PURE__ */ new Set();
    for (const row of rows) {
      const r = row;
      const signer = fold(r.from ?? r.from_address);
      if (signer === wallet) return null;
      const called = fold(r.to ?? r.to_address);
      const fabricated = legs(row).some(
        (leg) => fold(leg.from) === wallet && fabricatesSender(leg, called, evidence(row))
      );
      if (fabricated && signer) named.add(signer);
    }
    if (named.size > 0) {
      return {
        role: "spoofed",
        basis: "unsigned",
        transactions: rows.length,
        wallets: named.size,
        reason: `This address has signed none of the transactions loaded here, yet ${named.size} other signer${named.size === 1 ? "" : "s"} wrote token transfers naming it as the sender. That is the shape of an address-poisoning lookalike: the entry exists so the name is in somebody's history to copy. Compare it character by character against the address you mean to pay.`
      };
    }
  }
  if (spoofed > 0) {
    return {
      role: "spoofed",
      basis: "named",
      transactions: spoofed,
      wallets: signers.size,
      reason: `${spoofed} transaction${spoofed === 1 ? "" : "s"} loaded here name this address as the sender of token transfers it did not sign, written by ${signers.size} other signer${signers.size === 1 ? "" : "s"}. Those entries are not payments it made \u2014 they are how an address-poisoning campaign puts a name into somebody's history. Check any payment claimed from this address against a transaction it actually signed.`
    };
  }
  return null;
}
function addressFeedPoisoning(rows, subject, options = {}) {
  const wallet = fold(subject);
  const flags = /* @__PURE__ */ new Map();
  if (!wallet) return flags;
  const catalogued = foldedPoisoners(options.catalogPoisoners);
  const legs = rows.flatMap((row) => legsOf(row, wallet));
  const impersonations = deriveImpersonations(legs, (leg) => leg, [wallet], options.poisoningPolicy, catalogued);
  if (impersonations.size === 0 && !catalogued) return flags;
  for (const leg of legs) {
    if (flags.has(leg.rowId)) continue;
    const reason = poisoningReason(leg, impersonations, leg.fromAddress === wallet ? void 0 : catalogued);
    if (reason) flags.set(leg.rowId, transactionFlag("poisoning", reason));
  }
  return flags;
}
function addressFeedCounterparties(rows, subject) {
  const wallet = fold(subject);
  if (!wallet) return [];
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const row of rows) {
    const chainId = typeof row.chain_id === "string" ? row.chain_id : null;
    for (const leg of legsOf(row, wallet)) {
      for (const end of [leg.fromAddress, leg.toAddress]) {
        if (!end || end === wallet) continue;
        const key = `${chainId ?? ""}::${end}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ address: end, chainId });
      }
    }
  }
  return out;
}
var TRANSFER_TOPIC = "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef";
function transfersFromEvents(events) {
  const out = [];
  for (const event of events) {
    const topics = event.topics ?? [];
    if (!event.address || topics.length < 3 || (topics[0] ?? "").toLowerCase() !== TRANSFER_TOPIC) continue;
    const from = `0x${(topics[1] ?? "").slice(-40)}`;
    const to = `0x${(topics[2] ?? "").slice(-40)}`;
    const data = (event.data ?? "").trim();
    const value = topics.length === 3 ? /^0x[0-9a-f]*$/i.test(data) ? (raw(data === "0x" ? "0" : data) ?? 0n).toString() : null : null;
    out.push({ kind: topics.length === 3 ? "erc20" : "nft", from, to, value, token: event.address });
  }
  return out;
}
function bodyTransfers(body) {
  return transfersFromEvents(parseEvents(body));
}
function subjectTransferLegs(row, transfers, subjects) {
  const rowId = txRowId(row);
  if (!rowId) return [];
  const chainId = row.chain_id;
  const own = /* @__PURE__ */ new Set();
  for (const subject of subjects) {
    const key = fold(subject);
    if (key) own.add(key);
  }
  const timestamp = rowTimestamp(row);
  const out = [];
  for (const transfer of transfers) {
    const from = fold(transfer.from, chainId);
    const to = fold(transfer.to, chainId);
    if (!from || !to || !own.has(to) || own.has(from)) continue;
    const nft = transfer.kind === "nft";
    out.push(
      legCandidate(rowId, from, to, nft ? "erc721" : "erc20", false, {
        timestamp,
        token: transfer.token ?? null,
        amountRaw: nft ? null : amountText(transfer.value)
      })
    );
  }
  return out;
}
function txBodySpoofing(body) {
  const fromLogs = transfersFromEvents(body.events ?? []);
  return spoofedTransfers({
    signer: body.from,
    called: body.to,
    transfers: fromLogs.length > 0 ? fromLogs : body.transfers ?? [],
    impersonations: body.impersonations,
    catalogued: body.catalogued,
    unvouchedTokens: body.unvouchedTokens
  });
}
function txBodyFlag(body, extra = []) {
  const spoofing = txBodySpoofing(body);
  const airdrop = txBodyAirdrop(body);
  return strongestFlag([
    spoofing ? transactionFlag("poisoning", spoofing.reason) : null,
    airdrop ? transactionFlag("unsolicited_distribution", airdrop.reason) : null,
    ...extra
  ]);
}
function txBodyAirdrop(body) {
  const fromLogs = transfersFromEvents(body.events ?? []);
  return unsolicitedDistribution({
    signer: body.from,
    called: body.to,
    transfers: fromLogs.length > 0 ? fromLogs : body.transfers ?? []
  });
}

// src/dustFilter.ts
function txRowCandidate(row, getEnrichment) {
  const enrichment = getEnrichment?.(row);
  const protocols = rowProtocols(row);
  return {
    canonicalTxId: txRowId(row),
    valueUsd: row.value_usd ?? null,
    primaryProtocolId: row.primary_protocol_id ?? null,
    protocolIds: protocols,
    fromAddress: enrichment?.from ?? null,
    // `enrichment.to`, NOT `counterparty.address`. The counterparty is "the
    // row's `to`, or `from` on a one-ended row" — so on those rows it is the
    // SENDER, and feeding it here would count a duster as its own recipient.
    // The recipient spread is the entire point of the campaign check: it is what
    // separates a duster from a payroll contract paying the same fifty people.
    toAddress: enrichment?.to ?? null,
    // Native value is proof price is not needed for: a poisoner can mint a fake
    // token that "sends" anything, but cannot move ETH it does not hold. So a
    // funder whose payment the pipeline could not price is still genuine, and
    // its lookalike still caught. The dust rule does not read this.
    provenValue: nativeValueProves(row.chain_id, row.total_value_raw),
    // What the mirror test compares a real-value transfer IN against: the
    // subject's own payment, minutes before. A row's raw total is native, so it
    // names the asset only on a bare native transfer; any other row is
    // compared by `valueUsd`.
    timestamp: rowTimestamp(row),
    ...movesOnlyNative(protocols) ? { token: NATIVE_ASSET, amountRaw: nativeAmount(row.total_value_raw) } : {}
  };
}
function movesOnlyNative(protocols) {
  const claimed = protocols.filter((id) => id !== FALLBACK_PROTOCOL_ID);
  return claimed.length > 0 && claimed.every((id) => id === "native_transfer");
}
function nativeAmount(value) {
  if (typeof value === "string" && /^\d+$/.test(value.trim())) return value.trim();
  if (typeof value === "number" && Number.isSafeInteger(value) && value >= 0) return String(value);
  return null;
}
function withoutDustRows(rows, options = {}) {
  const read = (row) => txRowCandidate(row, options.getEnrichment);
  const dustSenders = options.dustSenders ?? (options.deriveSenders ? deriveDustSenders(rows, read, { minRecipients: options.campaignMinRecipients }) : void 0);
  return withoutDust(rows, read, {
    usdFloor: options.usdFloor,
    protectedIds: options.protectedIds,
    dustSenders
  });
}

// src/rowFlags.ts
function classifyTxRows(rows, options = {}) {
  const read = (row) => txRowCandidate(row, options.getEnrichment);
  const candidates = rows.map(read);
  const legs = rows.map((row) => options.legsOf?.(row) ?? []);
  const subjects = options.subjects ? [...options.subjects] : void 0;
  const subjectSet = /* @__PURE__ */ new Set();
  for (const subject of subjects ?? []) {
    const address = poisoningAddress(subject);
    if (address) subjectSet.add(address);
  }
  const catalogued = withoutSubjects(options.catalogPoisoners, subjectSet);
  const impersonations = subjects ? deriveImpersonations(
    [...candidates, ...legs.flat()],
    (c) => c,
    subjects,
    options.poisoningPolicy,
    catalogued
  ) : /* @__PURE__ */ new Map();
  const dustSenders = options.dustSenders ?? (options.deriveSenders ? deriveDustSenders(rows, read, { minRecipients: options.campaignMinRecipients }) : void 0);
  const flags = /* @__PURE__ */ new Map();
  rows.forEach((row, index) => {
    const id = txRowId(row);
    if (!id) return;
    const candidate = candidates[index];
    const transfers = options.transfersOf?.(row);
    const spoofing = subjects && transfers && !pricedAboveDust(candidate.valueUsd) && candidate.provenValue !== true ? spoofedTransfers({
      signer: candidate.fromAddress,
      called: candidate.toAddress,
      transfers,
      impersonations,
      catalogued,
      unvouchedTokens: options.unvouchedTokensOf?.(row)
    }) : null;
    const scopedSpoofing = spoofing?.legs.some(
      (leg) => subjectSet.has(poisoningAddress(leg.from) ?? "") || subjectSet.has(poisoningAddress(leg.to) ?? "")
    ) ? spoofing.reason : null;
    const poisoning = subjects ? poisoningReason(candidate, impersonations, catalogued) ?? firstPoisoningReason(legs[index], impersonations, catalogued) : null;
    const dust = dustReason(candidate, { usdFloor: options.usdFloor, dustSenders });
    const paid = paidLookalikeReason(candidate, impersonations, subjectSet, catalogued);
    const flag = strongestFlag([
      paid ? transactionFlag("paid_lookalike", paid) : null,
      poisoning ? transactionFlag("poisoning", poisoning) : null,
      scopedSpoofing ? transactionFlag("poisoning", scopedSpoofing) : null,
      !pricedAboveDust(candidate.valueUsd) && candidate.provenValue !== true ? poisoningGroupFlag(row, options.groupsOf?.(row) ?? []) : null,
      // The row's own record of the fakes it moved — no subject needed: a fake
      // USDT is one whoever's feed it is in.
      rowCounterfeitFlag(row),
      dust ? transactionFlag("dust", dust) : null
    ]);
    if (flag) flags.set(id, flag);
  });
  return { flags, impersonations };
}
function poisoningGroupFlag(row, groups) {
  const chain = typeof row.chain_id === "string" ? toCanonicalChainId(row.chain_id) : null;
  const hash = typeof row.tx_hash === "string" ? row.tx_hash.toLowerCase() : null;
  const canonical = typeof row.canonical_tx_id === "string" ? row.canonical_tx_id : null;
  for (const group of groups) {
    if (group.saga_kind !== "spam.address_poisoning" || group.state === "cancelled") continue;
    const bait = group.members?.some((member) => {
      const roles = member.roles?.length ? member.roles : [member.role];
      if (!roles.includes("dust_send") || roles.some((role) => role && role !== "dust_send")) return false;
      return Boolean(canonical && member.tx_id === canonical || chain && hash && member.chain_id && toCanonicalChainId(member.chain_id) === chain && member.tx_hash?.toLowerCase() === hash);
    });
    if (bait) return transactionFlag("poisoning", `The saved address-poisoning finding ${group.group_id} identifies this transaction as a bait transfer (dust_send). This is a detector finding, not proof of a loss; inspect its evidence before attributing funds.`);
  }
  return null;
}
function rowCounterfeitTokens(row) {
  const raw2 = row.counterfeit_tokens;
  if (!Array.isArray(raw2)) return [];
  return raw2.flatMap((entry) => parseTokenImpersonation(entry) ?? []);
}
function rowCounterfeitFlag(row) {
  return counterfeitTokenFlag(rowCounterfeitTokens(row));
}
function firstPoisoningReason(legs, impersonations, catalogued) {
  for (const leg of legs) {
    const reason = poisoningReason(leg, impersonations, catalogued);
    if (reason) return reason;
  }
  return null;
}
function withoutSubjects(catalogued, subjects) {
  if (!catalogued || catalogued.size === 0) return void 0;
  const out = /* @__PURE__ */ new Set();
  for (const raw2 of catalogued) {
    const address = poisoningAddress(raw2);
    if (address && !subjects.has(address)) out.add(address);
  }
  return out.size > 0 ? out : void 0;
}
function catalogPoisonersOf(rows, getEnrichment) {
  const out = /* @__PURE__ */ new Set();
  for (const row of rows) {
    const enrichment = getEnrichment(row);
    for (const end of [enrichment?.sender, enrichment?.counterparty]) {
      const address = poisoningAddress(end?.address, end?.chainId);
      if (!address) continue;
      if ((end?.tags ?? []).some((tag) => isPoisoningCatalogTag(tag.label))) out.add(address);
    }
  }
  return out;
}
function flaggedIds(classification, kind, protectedIds = /* @__PURE__ */ new Set()) {
  const out = /* @__PURE__ */ new Set();
  for (const [id, flag] of classification.flags) {
    if (flag.kind === kind && !protectedIds.has(id)) out.add(id);
  }
  return out;
}
function impersonationOf(classification, address) {
  const key = poisoningAddress(address);
  return key ? classification.impersonations.get(key)?.mimics : void 0;
}
var CANONICAL = DEFAULT_TX_COLUMNS;
function names(refs) {
  return (refs ?? []).map((ref) => ref.name ?? ref.label ?? "").filter(Boolean).join(", ");
}
function overlay(key, header, read) {
  return { key, header, value: (row) => read(getRowEnrichment(row)) ?? "" };
}
var TX_EXPORT_COLUMNS = [
  ...CANONICAL.map((c) => typeof c === "string" ? { key: c, header: fieldLabel(c) } : c),
  overlay(TX_FIELDS.from, fieldLabel(TX_FIELDS.from), (e) => e?.from),
  overlay(TX_FIELDS.to, fieldLabel(TX_FIELDS.to), (e) => e?.to),
  overlay(TX_FIELDS.direction, "Direction", (e) => e?.direction),
  // The PRICED value the cell shows. The row's own `value_usd` is frequently
  // null on a list row, so exporting it alone printed a blank column next to a
  // screen that was showing a number.
  overlay("value_usd_enriched", "Value (USD)", (e) => e?.valueUsd),
  overlay("tx_labels", fieldLabel("tx_labels"), (e) => names(e?.labels)),
  overlay("tx_tags", fieldLabel("tx_tags"), (e) => names(e?.tags)),
  overlay("tx_entity", fieldLabel("tx_entity"), (e) => e?.entity?.name),
  overlay("counterparty_entity", fieldLabel("counterparty_entity"), (e) => e?.counterparty?.entity?.name),
  overlay("counterparty_labels", fieldLabel("counterparty_labels"), (e) => names(e?.counterparty?.labels)),
  overlay("counterparty_tags", fieldLabel("counterparty_tags"), (e) => names(e?.counterparty?.tags)),
  { key: "counterparty_risk", header: fieldLabel("counterparty_risk"), value: (row) => resolveRisk(getRowEnrichment(row)) ?? "" },
  // The transaction's OWN risk, read off the row exactly as the `tx_risk` cell
  // reads it — level (never "safe" for a sub-band score), score, leading policy.
  { key: "tx_risk", header: fieldLabel("tx_risk"), value: (row) => txRowRisk(row)?.level ?? "" },
  { key: "tx_risk_score", header: "Risk score", value: (row) => txRowRisk(row)?.score ?? "" },
  { key: "tx_risk_policy", header: "Risk policy", value: (row) => txRowRisk(row)?.policyId ?? "" },
  {
    key: "counterfeit_tokens",
    header: "Counterfeit tokens",
    value: (row) => rowCounterfeitTokens(row).map((claim) => claim.claimed).join(", ")
  }
];
function TransactionExportMenu({
  rows,
  columns,
  formats,
  filenameBase = "transactions",
  label,
  disabled,
  exportContext,
  historyHref,
  onError
}) {
  return /* @__PURE__ */ jsx(
    ExportMenu,
    {
      rows,
      columns: columns ?? TX_EXPORT_COLUMNS,
      formats,
      filenameBase,
      label,
      disabled,
      exportContext: exportContext ?? { resource: "transactions" },
      historyHref,
      onError
    }
  );
}

// src/filters.ts
var TX_FILTER_OPS = {
  /** Membership in a set (multi-select facets: chain_id, finality, type). */
  IN: "in",
  /** Exact match. */
  EQ: "eq",
  /** Case-insensitive substring (hash, address). */
  CONTAINS: "contains",
  /** Numeric/temporal range bounds. */
  GTE: "gte",
  LTE: "lte",
  /** Catalog membership filters. */
  HAS_LABEL: "has_label",
  HAS_TAG: "has_tag",
  HAS_GROUP: "has_group",
  HAS_ENTITY: "has_entity"
};
var CLIENT_FILTER_OPS = /* @__PURE__ */ new Set([TX_FILTER_OPS.IN, TX_FILTER_OPS.CONTAINS]);
function asStringArray(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value == null || value === "") return [];
  return [String(value)];
}
function filterRulesToQuery(rules) {
  const query = {};
  const pushAll = (key, values) => {
    if (values.length === 0) return;
    const existing = Array.isArray(query[key]) ? query[key] : [];
    query[key] = [...existing, ...values];
  };
  for (const rule of rules ?? []) {
    switch (rule.op) {
      case TX_FILTER_OPS.IN:
        pushAll(rule.field, asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.EQ:
      case TX_FILTER_OPS.CONTAINS:
        if (rule.value != null && rule.value !== "") query[rule.field] = String(rule.value);
        break;
      case TX_FILTER_OPS.GTE:
        if (rule.value != null) query[`${rule.field}_gte`] = String(rule.value);
        break;
      case TX_FILTER_OPS.LTE:
        if (rule.value != null) query[`${rule.field}_lte`] = String(rule.value);
        break;
      case TX_FILTER_OPS.HAS_LABEL:
        pushAll("label", asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.HAS_TAG:
        pushAll("tag", asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.HAS_GROUP:
        pushAll("group", asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.HAS_ENTITY:
        if (rule.value != null && rule.value !== "") query.entity_id = String(rule.value);
        break;
    }
  }
  return query;
}
function filterRulesToColumnFilters(rules) {
  const filters = [];
  for (const rule of rules ?? []) {
    if (!CLIENT_FILTER_OPS.has(rule.op)) continue;
    if (rule.op === TX_FILTER_OPS.IN) {
      const values = asStringArray(rule.value);
      if (values.length) filters.push({ id: rule.field, value: values });
    } else if (rule.value != null && rule.value !== "") {
      filters.push({ id: rule.field, value: String(rule.value) });
    }
  }
  return filters;
}
function columnFiltersToFilterRules(filters) {
  const rules = [];
  for (const f of filters) {
    if (Array.isArray(f.value)) {
      const values = f.value.map(String).filter(Boolean);
      if (values.length) rules.push({ field: f.id, op: TX_FILTER_OPS.IN, value: values });
    } else if (typeof f.value === "string" && f.value !== "") {
      rules.push({ field: f.id, op: TX_FILTER_OPS.CONTAINS, value: f.value });
    }
  }
  return rules;
}
var TRANSACTIONS_TABLE_ID = "transactions";
var TX_FILTER_CODEC = {
  toRules: columnFiltersToFilterRules,
  fromRules: filterRulesToColumnFilters
};
function useTransactionViews(options) {
  return useTableViews({
    ...options,
    tableId: TRANSACTIONS_TABLE_ID,
    filterCodec: TX_FILTER_CODEC
  });
}
function TransactionTable({
  rows,
  columns,
  onDrilldown,
  getTxHref,
  getAddressHref,
  isLoading = false,
  isFetching = false,
  error,
  onRetry,
  onRowClick,
  activeRowId,
  enableSelection = false,
  enableToolbar = true,
  searchPlaceholder = "Search by hash, address or entity\u2026",
  serverSearch,
  facetedFilters,
  facets,
  toolbarActions,
  enableSavedViews = false,
  enableExport = false,
  exportRows,
  exportFilenameBase,
  exportContext,
  exportHistoryHref,
  selectionActions,
  pagination,
  viewKey,
  defaultColumnVisibility,
  columnVisibility,
  onColumnVisibilityChange,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  onSortingChange,
  columnOrder,
  onColumnOrderChange,
  enableColumnReorder = true,
  density,
  rowHeight = 44,
  enableColumnResizing = false,
  rowClassName,
  onEndReached,
  live,
  liveMode,
  onLiveModeChange,
  liveAvailable = true,
  liveModes,
  liveHidden = false,
  liveUnavailableReason,
  showLegend = false,
  getEnrichment = getRowEnrichment,
  renderRowDetail,
  detailTitle,
  detailEyebrow = "Transaction",
  detailSubtitle,
  detailFooter,
  height = "h-[600px]",
  emptyTitle = "No transactions",
  emptyDescription = "There are no transactions to show here yet.",
  emptyAction,
  className
}) {
  const resolvedColumns = React10.useMemo(
    () => columns ?? createTransactionColumns({ onDrilldown, getTxHref, getAddressHref }),
    [columns, onDrilldown, getTxHref, getAddressHref]
  );
  const hostControlsState = columnVisibility !== void 0 || columnFilters !== void 0 || sorting !== void 0 || columnOrder !== void 0;
  const savedViews = useTableSavedViews({
    enabled: enableSavedViews && !hostControlsState,
    tableId: viewKey ?? TRANSACTIONS_TABLE_ID,
    columnIds: React10.useMemo(() => columnIds(resolvedColumns), [resolvedColumns]),
    filterCodec: TX_FILTER_CODEC
  });
  const renderToolbarActions = (table) => /* @__PURE__ */ jsxs(Fragment, { children: [
    savedViews.toolbar,
    enableExport && /* @__PURE__ */ jsx(
      TransactionExportMenu,
      {
        rows: exportRows ?? (() => table.getFilteredRowModel().rows.map((row) => row.original)),
        exportContext: {
          resource: "transactions",
          ...savedViews.exportContext,
          ...exportContext
        },
        ...exportFilenameBase ? { filenameBase: exportFilenameBase } : {},
        ...exportHistoryHref ? { historyHref: exportHistoryHref } : {}
      }
    ),
    toolbarActions
  ] });
  const renderFacets = React10.useCallback(
    () => /* @__PURE__ */ jsxs(Fragment, { children: [
      onLiveModeChange && /* @__PURE__ */ jsx(
        LiveModeControl,
        {
          mode: liveMode ?? "off",
          onChange: onLiveModeChange,
          liveAvailable,
          modes: liveModes,
          liveHidden,
          liveUnavailableReason,
          size: "sm"
        }
      ),
      live,
      facetedFilters
    ] }),
    [onLiveModeChange, liveMode, liveAvailable, liveHidden, liveUnavailableReason, live, facetedFilters]
  );
  const renderLegend = React10.useCallback(
    (table) => {
      const filtered = table.getFilteredRowModel().rows.map((row) => row.original);
      if (filtered.length === 0) return null;
      return /* @__PURE__ */ jsx(TransactionLegend, { rows: filtered, getEnrichment });
    },
    [getEnrichment]
  );
  return /* @__PURE__ */ jsx(
    ResourceTable,
    {
      rows,
      columns: resolvedColumns,
      getRowId: txSelectionRowId,
      isLoading,
      isFetching,
      error,
      onRetry,
      toolbar: enableToolbar ? {
        searchColumn: "tx_hash",
        searchPlaceholder,
        serverSearch,
        columnLabels: TX_COLUMN_LABELS,
        filterColumns: facets?.facets ?? DEFAULT_TX_FACETS,
        filterOverrides: transactionFilterOverrides(facets?.options, facets?.labels)
      } : false,
      facets: renderFacets,
      toolbarActions: renderToolbarActions,
      banner: showLegend ? renderLegend : void 0,
      selectionActions: enableSelection ? selectionActions : void 0,
      enableRowSelection: enableSelection,
      enableColumnResizing,
      pagination,
      viewKey,
      defaultColumnVisibility,
      columnVisibility: columnVisibility ?? savedViews.controlled.columnVisibility,
      onColumnVisibilityChange: onColumnVisibilityChange ?? savedViews.controlled.onColumnVisibilityChange,
      columnFilters: columnFilters ?? savedViews.controlled.columnFilters,
      onColumnFiltersChange: onColumnFiltersChange ?? savedViews.controlled.onColumnFiltersChange,
      sorting: sorting ?? savedViews.controlled.sorting,
      onSortingChange: onSortingChange ?? savedViews.controlled.onSortingChange,
      columnOrder: columnOrder ?? savedViews.controlled.columnOrder,
      onColumnOrderChange: onColumnOrderChange ?? savedViews.controlled.onColumnOrderChange,
      enableColumnReorder,
      density: density ?? savedViews.density,
      rowHeight,
      height,
      activeRowId,
      rowClassName,
      ...onEndReached ? { onEndReached } : {},
      onRowClick,
      renderRowDetail,
      detailTitle,
      detailEyebrow,
      detailSubtitle,
      detailFooter,
      emptyTitle,
      emptyDescription,
      ...emptyAction ? { emptyAction } : {},
      className
    }
  );
}
var NO_TX_SERVER_FILTERS = Object.freeze({});
function valueOf(filters, id) {
  return asTrimmedString(filters?.[id]) ?? null;
}
function activeTxServerFilters(filters) {
  return TX_INDEXED_FILTERS.filter((id) => valueOf(filters, id) !== null);
}
function txServerFilterQuery(filters) {
  const out = {};
  const chain = valueOf(filters, "chain");
  const protocol = valueOf(filters, "protocol");
  const entity = valueOf(filters, "entity");
  if (chain) out[TX_INDEXED_FILTER_PARAMS.chain] = [toStoredChainId(chain) ?? chain];
  if (protocol) out[TX_INDEXED_FILTER_PARAMS.protocol] = [protocol];
  if (entity) out[TX_INDEXED_FILTER_PARAMS.entity] = entity;
  return out;
}
function rowMatchesTxServerFilters(row, filters) {
  const active = activeTxServerFilters(filters);
  if (active.length === 0) return true;
  if (typeof row !== "object" || row === null) return false;
  const r = row;
  const chain = valueOf(filters, "chain");
  if (chain) {
    const rowChain = asString(r.chain_id);
    if (!rowChain || (toStoredChainId(rowChain) ?? rowChain) !== (toStoredChainId(chain) ?? chain)) return false;
  }
  const protocol = valueOf(filters, "protocol");
  if (protocol) {
    const wanted = protocol.toLowerCase();
    const ids = rowProtocols(r);
    if (!ids.some((id) => id.toLowerCase() === wanted)) return false;
  }
  const entity = valueOf(filters, "entity");
  if (entity && asString(r.primary_entity_id) !== entity) return false;
  return true;
}
function txRowKey(row) {
  return txStreamKey(row);
}
function txFeedTime(row) {
  const raw2 = row.block_timestamp;
  let value;
  if (typeof raw2 === "number") value = raw2;
  else if (typeof raw2 === "string" && raw2.trim() !== "") {
    const n = Number(raw2);
    value = Number.isFinite(n) ? n : Date.parse(raw2) / 1e3;
  } else return Number.POSITIVE_INFINITY;
  if (!Number.isFinite(value) || value <= 0) return Number.POSITIVE_INFINITY;
  return value > 1e12 ? value / 1e3 : value;
}
function overlay2(fetched, streamed) {
  const out = { ...fetched };
  for (const [key, value] of Object.entries(streamed)) {
    if (value === void 0 || value === null || value === "") continue;
    out[key] = value;
  }
  return out;
}
function mergeTransactionRowsInFeedOrder(streamed, fetched, options = {}) {
  const { max = 1e3, hasNewer = false, hasOlder = false, deleted } = options;
  const index = /* @__PURE__ */ new Map();
  let removed = 0;
  const rows = [];
  for (const row of fetched) {
    const key = txRowKey(row);
    if (key !== null) {
      if (deleted?.has(key)) {
        removed += 1;
        continue;
      }
      const at = index.get(key);
      if (at !== void 0) {
        rows[at] = overlay2(rows[at], row);
        continue;
      }
      index.set(key, rows.length);
    }
    rows.push(row);
  }
  const times = rows.map(txFeedTime).filter(Number.isFinite);
  const newest = times.length ? Math.max(...times) : Number.POSITIVE_INFINITY;
  const oldest = times.length ? Math.min(...times) : Number.NEGATIVE_INFINITY;
  let updated = 0;
  const fresh = [];
  const seen = /* @__PURE__ */ new Set();
  for (const row of streamed) {
    const key = txRowKey(row);
    if (key !== null) {
      if (seen.has(key) || deleted?.has(key)) continue;
      seen.add(key);
      const at = index.get(key);
      if (at !== void 0) {
        rows[at] = overlay2(rows[at], row);
        updated += 1;
        continue;
      }
    }
    const t = txFeedTime(row);
    if (fetched.length > 0 && hasOlder && t < oldest) continue;
    if (fetched.length > 0 && hasNewer && Number.isFinite(t) && t > newest) continue;
    fresh.push(row);
  }
  for (const row of fresh) {
    const t = txFeedTime(row);
    let at = rows.findIndex((existing) => txFeedTime(existing) <= t);
    if (at < 0) at = rows.length;
    rows.splice(at, 0, row);
  }
  return { rows: rows.slice(0, max), inserted: fresh.length, updated, removed };
}
function mergeTransactionRows(streamed, fetched, max = 1e3, deleted) {
  return mergeTransactionRowsInFeedOrder(streamed, fetched, { max, deleted }).rows;
}
function liveRowNumbers(row) {
  const usd = row.value_usd;
  if (typeof usd !== "string") return row;
  const n = usd.trim() === "" ? NaN : Number(usd);
  return { ...row, value_usd: Number.isFinite(n) ? n : null };
}
function defaultParse(message) {
  const payload = message.payload;
  if (!payload || typeof payload !== "object") return null;
  if (payload.tx_hash == null && payload["canonical_tx_id"] == null) return null;
  return payload;
}
function useTransactionStream(options) {
  const { channel, enabled = true, max = 500, parse } = options;
  const parseRow = React10.useCallback(
    (message) => {
      const row = (parse ?? defaultParse)(message);
      return row ? liveRowNumbers(row) : null;
    },
    [parse]
  );
  return useResourceStream({
    channel,
    rowKey: txRowKey,
    parse: parseRow,
    enabled,
    max
  });
}
function useChainHeads(options) {
  const tips = useChainTips(options);
  return React10.useMemo(
    () => Object.fromEntries(Object.values(tips).map((tip) => [tip.chainId, tip.headBlock])),
    [tips]
  );
}
function useChainHeadReader(heads) {
  const ref = React10.useRef(heads);
  ref.current = heads;
  return React10.useCallback((chainId) => {
    const key = toCanonicalChainId(chainId) ?? chainId.toLowerCase();
    return ref.current[key] ?? ref.current[chainId] ?? null;
  }, []);
}
function canonicalHeads(heads) {
  const out = {};
  for (const [id, head] of Object.entries(heads)) {
    out[toCanonicalChainId(id) ?? id.toLowerCase()] = head;
  }
  return out;
}
function withLiveFinality(rows, heads, now) {
  const byChain = canonicalHeads(heads);
  let moved = false;
  const next = rows.map((row) => {
    if (row.finality == null) return row;
    const chainId = String(row.chain_id ?? "").toLowerCase();
    const headKey = toCanonicalChainId(chainId) ?? chainId;
    const finality = resolveFinality({
      rowFinality: row.finality,
      blockNumber: row.block_number ?? null,
      head: byChain[headKey] ?? null,
      blockTimestamp: row.block_timestamp ?? null,
      now,
      chainOrId: chainId
    });
    if (finality === row.finality) return row;
    moved = true;
    return { ...row, finality };
  });
  return moved ? next : rows;
}
function useLiveFinalityRows(rows, heads) {
  const now = useNow();
  return React10.useMemo(() => withLiveFinality(rows, heads, now), [rows, heads, now]);
}
var NO_NOTICES = Object.freeze([]);
var log = createLogger("tx-table:query");
var EXPORT_ROW_CEILING = 1e5;
function useTransactionsQuery(options = {}) {
  const { resource = "transactions", pageSize = 50, query, enabled = true, notifyOnError = false } = options;
  const serializedQuery = JSON.stringify(query ?? {});
  const [cursorStack, setCursorStack] = React10.useState([]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  if (useDepsChanged([serializedQuery, resource, pageSize])) {
    setCursorStack((stack) => stack.length === 0 ? stack : []);
  }
  const result = useQuery(
    (client2, signal) => client2.get(`/${resource}`, {
      query: { ...query ?? {}, limit: pageSize, cursor: cursor ?? void 0 },
      signal
    }),
    {
      enabled,
      deps: [cursor, serializedQuery, resource, pageSize],
      // Every dep here describes the SAME list — a cursor, a filter, a page
      // size — so holding the current page while the next one loads reads as a
      // page turn rather than the table blinking out. (A detail read keyed by a
      // record id must NOT do this: see `useQuery`'s note.) Consumers show the
      // in-flight state from `isFetching`.
      keepPreviousData: true,
      onError: (envelope2) => {
        log.error("transactions fetch failed", {
          resource,
          status: envelope2.statusCode,
          code: envelope2.error.error_code,
          correlationId: envelope2.error.correlation_id
        });
        if (notifyOnError) notifyError(envelope2);
      }
    }
  );
  const payload = result.data;
  const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.items) ? payload.items : [];
  const pageInfo = (Array.isArray(payload) ? void 0 : payload?.pagination) ?? result.envelope?.pagination;
  const stats = (Array.isArray(payload) ? void 0 : payload?.metadata?.stats) ?? {};
  const envelope = result.envelope;
  const notices = React10.useMemo(() => {
    const found = txListNoticesOf(Array.isArray(payload) ? void 0 : payload, envelope);
    return found.length > 0 ? found : NO_NOTICES;
  }, [payload, envelope]);
  const nextCursor = pageInfo?.next ?? null;
  const hasNext = Boolean(nextCursor);
  const hasPrev = cursorStack.length > 0;
  const goNext = React10.useCallback(() => {
    if (nextCursor) setCursorStack((stack) => [...stack, nextCursor]);
  }, [nextCursor]);
  const goPrev = React10.useCallback(() => {
    setCursorStack((stack) => stack.slice(0, -1));
  }, []);
  const reset = React10.useCallback(() => setCursorStack([]), []);
  const client = useApiClient();
  const fetchAllRows = React10.useCallback(
    async function* walk(opts = {}) {
      const max = Math.max(1, opts.maxRows ?? EXPORT_ROW_CEILING);
      const limit = Math.max(1, opts.pageSize ?? pageSize);
      const filters = JSON.parse(serializedQuery);
      let next;
      let seen = 0;
      for (; ; ) {
        const response = await client.get(`/${resource}`, {
          query: { ...filters, limit, cursor: next },
          ...opts.signal ? { signal: opts.signal } : {}
        });
        if (!response.ok) {
          throw new Error(
            `couldn\u2019t read every page for this export (${response.error.message})`
          );
        }
        const body = response.data;
        const page = Array.isArray(body) ? body : Array.isArray(body?.items) ? body.items : [];
        for (const row of page) {
          if (seen >= max) {
            opts.onTruncated?.(seen);
            return;
          }
          seen += 1;
          yield row;
        }
        const info = (Array.isArray(body) ? void 0 : body?.pagination) ?? response.pagination;
        next = info?.next ?? void 0;
        if (!next || page.length === 0) return;
      }
    },
    [client, resource, pageSize, serializedQuery]
  );
  return {
    rows,
    total: pageInfo?.total,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    hasNext,
    hasPrev,
    goNext,
    goPrev,
    reset,
    refetch: result.refetch,
    pagination: { hasNext, hasPrev, onNext: goNext, onPrev: goPrev },
    stats,
    notices,
    fetchAllRows
  };
}
var log2 = createLogger("tx-table:detail");
var POLL_INTERVAL_MS = 2500;
var MAX_POLL_ATTEMPTS = 48;
function useTransactionDetail(canonicalTxId, options = {}) {
  const { resource = "transactions", includeBody = false, enabled = true, notifyOnError = false } = options;
  const [activeScope, setActiveScope] = React10.useState(options.initialScope ?? null);
  const [pollTarget, setPollTarget] = React10.useState(null);
  const [enrichmentTimedOut, setEnrichmentTimedOut] = React10.useState(false);
  const attemptsRef = React10.useRef(0);
  const overridesKey = JSON.stringify(activeScope?.overrides ?? null);
  const subjectChanged = useDepsChanged([canonicalTxId, JSON.stringify(options.initialScope ?? null)]);
  const scopeChanged = useDepsChanged([overridesKey]);
  if (subjectChanged) setActiveScope(options.initialScope ?? null);
  if (subjectChanged || scopeChanged) {
    setPollTarget(null);
    setEnrichmentTimedOut(false);
    attemptsRef.current = 0;
  }
  const result = useQuery(
    (client, signal) => {
      const readId = pollTarget ?? canonicalTxId ?? "";
      const query = scopeReadQuery(activeScope, {
        includeBody,
        enqueue: pollTarget ? false : options.enqueue
      });
      return client.get(`/${resource}/${encodeURIComponent(readId)}`, {
        query,
        signal,
        // Opt out of the client's default 30s GET cache. This read backs the
        // surfaces that WRITE to the transaction — the inspector's attribution
        // chips, its notes and status — and each of them refetches on success to
        // show the result. A cached body turns that refetch into a no-op, so an
        // attached label reappears nowhere until the entry ages out, which reads
        // as "the write didn't take". The enrichment poll below has the same
        // problem in slower motion: successive polls share a key, so it would
        // poll the cache rather than the row.
        //
        // The entry can't simply be invalidated after a write either: the read
        // may be keyed by `chain:hash` while the writer only knows the canonical
        // id, and an in-flight GET that started pre-write will repopulate the
        // cache after any invalidation. Not caching a single-record read that is
        // already deduped by `useQuery` is the cheaper correctness.
        cacheTtlMs: 0
      });
    },
    {
      resourcePolicy: { resources: CATALOG_RESOURCES },
      enabled: enabled && Boolean(canonicalTxId),
      // The SUBJECT only. `pollTarget` is deliberately not here: it names the
      // same transaction, and as a dep it would reset the read (and the page's
      // preview) every time polling starts. The fetcher reads it at call time.
      deps: [canonicalTxId, overridesKey, includeBody, options.enqueue, resource],
      onError: (envelope2) => {
        log2.error("transaction detail fetch failed", {
          resource,
          status: envelope2.statusCode,
          code: envelope2.error.error_code,
          correlationId: envelope2.error.correlation_id
        });
        if (notifyOnError) notifyError(envelope2);
      }
    }
  );
  const raw2 = result.data;
  const detail = React10.useMemo(() => {
    if (raw2 == null) return void 0;
    const parsed = TransactionDetailSchema.safeParse(raw2);
    if (parsed.success) return parsed.data;
    log2.warn("transaction detail shape mismatch; using raw payload", {
      issues: parsed.error.issues.length
    });
    return raw2;
  }, [raw2]);
  const envelope = result.envelope;
  const debugResources = React10.useMemo(() => readDebugResources(envelope), [envelope]);
  const enrichmentStatus = detail?.enrichment_status ?? null;
  const isEnriching = enrichmentStatus === "processing";
  const enrichmentFailed = enrichmentStatus === "failed";
  React10.useEffect(() => {
    if (!isEnriching) return;
    const target = detail?.chain_id && detail?.tx_hash ? `${detail.chain_id}:${detail.tx_hash}` : canonicalTxId ?? null;
    setPollTarget((prev) => prev === target ? prev : target);
  }, [isEnriching, detail?.chain_id, detail?.tx_hash, canonicalTxId]);
  React10.useEffect(() => {
    if (!isEnriching || !pollTarget) return;
    if (result.isFetching) return;
    if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
      setEnrichmentTimedOut(true);
      return;
    }
    const timer = window.setTimeout(() => {
      attemptsRef.current += 1;
      result.refetch();
    }, POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [isEnriching, pollTarget, result.isFetching, result.refetch]);
  const refetchDetail = result.refetch;
  const retryEnrichment = React10.useCallback(() => {
    attemptsRef.current = 0;
    setEnrichmentTimedOut(false);
    setPollTarget(null);
    refetchDetail();
  }, [refetchDetail]);
  return {
    detail,
    debugResources,
    otherScopes: detail?.other_scopes ?? [],
    activeScope,
    selectScope: setActiveScope,
    enrichmentStatus,
    isEnriching,
    enrichmentFailed,
    enrichmentTimedOut,
    retryEnrichment,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch
  };
}
var TRANSACTION_DEBUG_RESOURCE = "transaction";
function buildGroup(stats) {
  const fields = [
    {
      key: "decoder_version",
      label: "Decoder version",
      value: stats.decoderVersion ?? null,
      kind: "text",
      hint: "The decode_transaction build that produced this decode."
    },
    {
      key: "catalog_version",
      label: "Catalogue version",
      value: stats.catalogVersion ?? null,
      kind: "text",
      hint: "The authe-commons build whose protocol catalogue was in play. It moves independently of the decoder, and a change in WHICH protocol a transaction was attributed to is usually explained here, not above. Unset on bodies written before the catalogue build was stamped."
    },
    {
      key: "semantic_schema_version",
      label: "Semantic schema",
      value: stats.semanticSchemaVersion ?? null,
      kind: "text"
    },
    {
      key: "decoded_at",
      label: "Decoded at",
      value: stats.decodedAt ?? null,
      kind: "timestamp",
      hint: "When the decode ran \u2014 not when the transaction was mined."
    }
  ];
  if (!stats.decoderVersion && !stats.decodedAt && !stats.semanticSchemaVersion) {
    return {
      key: "decode_build",
      label: "Decode build",
      fields: [
        {
          key: "decode_meta",
          label: "Build",
          value: null,
          kind: "text",
          hint: "This body carries no decode_meta block \u2014 it predates the decode provenance record, so which build produced it is not recoverable."
        }
      ]
    };
  }
  return { key: "decode_build", label: "Decode build", fields };
}
function timingGroup(stats) {
  const fields = [
    {
      key: "decode_duration",
      label: "Decode duration",
      value: stats.durationMs ?? null,
      kind: "duration_ms"
    },
    ...Object.entries(stats.timings).map(
      ([phase, ms]) => ({
        key: `timing_${phase}`,
        // `call_ms` → "call" — the unit is already carried by the kind.
        label: phase.replace(/_ms$/, "").replace(/_/g, " "),
        value: ms,
        kind: phase.endsWith("_count") ? "number" : "duration_ms"
      })
    )
  ];
  if (Object.keys(stats.timings).length === 0) {
    const profiledOff = stats.decoderVersion !== void 0;
    return {
      key: "decode_timings",
      label: "Decode timings",
      fields: [
        {
          key: "decode_timings_absent",
          label: "Timings",
          value: null,
          kind: "text",
          hint: profiledOff ? "This decode ran with emit_decode_meta off, so only the build identity above was recorded \u2014 the per-phase breakdown was not." : "This body carries no decode_meta \u2014 it predates the timing record."
        }
      ]
    };
  }
  return { key: "decode_timings", label: "Decode timings", fields };
}
function configGroup(cfg) {
  const phases = Object.entries(cfg.phases);
  const deps = Object.entries(cfg.abiDeps);
  if (phases.length === 0 && deps.length === 0 && cfg.missingAbiTiers.length === 0 && !cfg.family && !cfg.proxyOnchainRpc) {
    return null;
  }
  const fields = [
    ...cfg.family ? [{ key: "chain_family", label: "Chain family", value: cfg.family, kind: "text" }] : [],
    ...cfg.proxyOnchainRpc ? [{
      key: "proxy_onchain_rpc",
      label: "On-chain proxy RPC",
      value: cfg.proxyOnchainRpc,
      kind: "text",
      hint: "Recorded capability at processing time; wired clients do not imply that network calls were allowed or made."
    }] : [],
    // Lead with what could not run: it is the finding, where the rest is
    // context. Absent when every tier was wired, which is itself the answer.
    ...cfg.missingAbiTiers.length ? [
      {
        key: "missing_abi_tiers",
        label: "ABI tiers unavailable",
        value: cfg.missingAbiTiers,
        kind: "json",
        hint: "Resolution tiers that could not run, and the dependency each was missing."
      }
    ] : [],
    ...phases.map(([name, phase]) => ({
      key: `phase_${name}`,
      label: name.replace(/_/g, " "),
      value: phase.value ? "on" : "off",
      kind: "text",
      // "off by default" and "off because someone set it" are different
      // findings; the source is what separates them.
      hint: phase.source ? `from ${phase.source}` : void 0
    })),
    ...deps.map(([dep, state]) => ({
      key: `abi_dep_${dep}`,
      label: dep,
      value: state,
      kind: "text"
    }))
  ];
  return { key: "decode_config", label: "Decode config", fields };
}
function stepGroup(stats) {
  if (stats.steps.length === 0) {
    return {
      key: "pipeline_steps",
      label: "Pipeline steps",
      fields: [
        {
          key: "pipeline_steps_absent",
          label: "Steps",
          value: null,
          kind: "text",
          hint: "No enrichment layers on this body. Either the decoded slice was offloaded and not merged into the copy loaded here, or the body predates the per-step timeline."
        }
      ]
    };
  }
  return {
    key: "pipeline_steps",
    label: `Enrichment timeline (${stats.steps.length})`,
    fields: stats.steps.map(
      (step) => ({
        key: `step_${step.name}`,
        label: step.name,
        value: [
          step.version ? `v${step.version}` : null,
          step.offsetMs === void 0 ? null : `+${Math.round(step.offsetMs)}ms from first enrichment`
        ].filter(Boolean).join(" \xB7 "),
        kind: "text",
        hint: [
          "Emission time offset, not step duration. Steps without enrichments are not shown.",
          step.kinds.length ? step.kinds.join(", ") : null,
          step.confidence === void 0 ? null : `lowest scored confidence ${step.confidence}`,
          step.unscoredCount ? `${step.unscoredCount} unscored output${step.unscoredCount === 1 ? "" : "s"}` : null
        ].filter(Boolean).join(" \u2014 ")
      })
    )
  };
}
function bodyDebugGroups(body) {
  if (!body || typeof body !== "object") return [];
  const stats = parseProcessingStats(body);
  const config = parseDecodeConfig(body);
  return [...executionGroups(body), buildGroup(stats), configGroup(config), timingGroup(stats), stepGroup(stats)].filter(
    (g) => g !== null
  );
}
function executionGroups(body) {
  const record = parsePipelineExecution(body);
  const object = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const preset = object(record?.preset);
  const applied = object(record?.applied_configuration);
  const policy = object(applied.confidence_policy);
  const resolved = object(policy.resolved);
  const gate = parseConfidenceGate(body);
  const field = (key, label, value, kind = "json", hint) => ({ key, label, value: value ?? null, kind, hint });
  return [
    { key: "pipeline_execution", label: "Stored pipeline execution", fields: record ? [
      field("pipeline_id", "Pipeline definition", record.pipeline_id, "id"),
      field("pipeline_revision", "Definition revision", record.pipeline_revision, "text"),
      field("preset", "Curated preset", preset.name, "text", "A preset names configured work, not a confidence guarantee. Custom definitions have no curated preset."),
      field("latency_tier", "Latency tier", record.latency_tier, "text"),
      field("body_config_hash", "Body config hash", record.config_hash, "id"),
      field("enabled_nodes", "Enabled nodes", record.enabled_nodes, "json", "Configured nodes; runtime conditions can still skip them. The enrichment timeline is not a complete execution trace."),
      field("feature_flags", "Pipeline feature flags", record.feature_flags, "json", "Flags pinned for processing, separate from today's customer UI feature flags."),
      field("execution", "Execution configuration", record.execution, "json", "Null switches inherit the pipeline's node and flag settings."),
      field("persistence", "Storage configuration", record.persistence, "json", "Null values inherit runtime defaults. This records configuration, not proof that each output was written."),
      field("applied_configuration", "Applied scope settings", applied)
    ] : [field("provenance_missing", "Execution snapshot", null, "text", "No supported execution snapshot on this body. Its historical policy cannot be recovered from current settings. Older bodies need reprocessing; newer formats may need an updated reader.")] },
    { key: "confidence_policy", label: "Confidence policy at processing", fields: [
      field("policy_hash", "Policy hash", resolved.hash ?? gate?.policyHash, "id"),
      field("effective_policy", "Effective policy", resolved.effective, "json", "Captured at processing time. Per-domain defaults and legacy overrides are identified on each decision; editing today's policy does not change this stored outcome."),
      field("policy_sources", "Setting sources", resolved.sources),
      field("policy_fallback", "Used fallback policy", resolved.fallback === void 0 ? null : String(resolved.fallback), "text"),
      field("gate_evaluated", "Evaluations by domain", gate?.evaluated),
      field("gate_outcomes", "Decisions and score calculations", gate?.outcomes),
      field("gate_omitted", "Decisions beyond body limit", gate?.omitted, "number"),
      field("gate_truncated", "Decisions beyond ledger limit", gate?.truncated, "number")
    ] }
  ];
}
function useTransactionDebug({
  canonicalTxId,
  debugResources = [],
  body,
  enabled = true
}) {
  const bodyGroups = React10.useMemo(() => bodyDebugGroups(body), [body]);
  const served = debugResources.find((r) => r.resource === TRANSACTION_DEBUG_RESOURCE);
  const groups = [...served?.groups ?? [], ...bodyGroups];
  const entry = enabled && groups.length > 0 ? {
    resource: TRANSACTION_DEBUG_RESOURCE,
    label: "Transaction",
    id: served?.id ?? canonicalTxId ?? null,
    groups
  } : null;
  useDebugResource(entry);
}
function toTokenFlow(tx, options) {
  return {
    flows: toTokenFlowRows(tx.token_flows ?? [], options),
    net: toNetFlowRows(tx.net_flows ?? [], options)
  };
}

// src/useGroupView.ts
var log3 = createLogger("tx-table:group");
function useGroupView(groupId, options = {}) {
  const { scope, resource = "groups", includeScene = true, limit, enabled = true, notifyOnError = false } = options;
  const result = useQuery(
    (client, signal) => client.get(`/${resource}/${encodeURIComponent(groupId ?? "")}`, {
      query: {
        // Only send include_scene when turning it OFF — the backend defaults on.
        scope,
        include_scene: includeScene ? void 0 : "false",
        limit: limit ?? void 0
      },
      signal
    }),
    {
      enabled: enabled && Boolean(groupId),
      deps: [groupId, includeScene, limit, resource, scope],
      onError: (envelope) => {
        log3.error("group view fetch failed", {
          resource,
          status: envelope.statusCode,
          code: envelope.error.error_code,
          correlationId: envelope.error.correlation_id
        });
        if (notifyOnError) notifyError(envelope);
      }
    }
  );
  const payload = result.data;
  const group = React10.useMemo(() => {
    if (payload?.scene == null) return void 0;
    try {
      return parseGroupView(payload);
    } catch (err) {
      log3.warn("group view parse failed; rendering members only", { error: String(err) });
      return void 0;
    }
  }, [payload]);
  const transactions = React10.useMemo(
    () => coerceTransactionRows(payload?.transactions ?? []),
    [payload]
  );
  return {
    group,
    transactions,
    descriptor: payload?.group,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch
  };
}
function channelKey(channel) {
  return typeof channel === "string" ? channel : JSON.stringify(channel);
}
function matchesGroup(message, groupId) {
  const payload = message.payload;
  if (!payload || typeof payload !== "object") return false;
  if (String(payload.id ?? "") === groupId && groupId !== "") return true;
  const header = payload.header;
  if (header && typeof header === "object" && String(header.group_id ?? "") === groupId && groupId !== "") {
    return true;
  }
  return false;
}
function useGroupViewLive(groupId, options = {}) {
  const { channels, debounceMs = 400, ...viewOptions } = options;
  const base = useGroupView(groupId, viewOptions);
  const client = useOptionalStreamingClient();
  const [lastEventAt, setLastEventAt] = React10.useState(null);
  const [eventCount, setEventCount] = React10.useState(0);
  if (useDepsChanged([groupId])) {
    setLastEventAt(null);
    setEventCount(0);
  }
  const refetchRef = React10.useRef(base.refetch);
  refetchRef.current = base.refetch;
  const timer = React10.useRef(null);
  const list2 = React10.useMemo(
    () => channels == null ? [] : Array.isArray(channels) ? [...channels] : [channels],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      channels == null ? "" : Array.isArray(channels) ? channels.map(channelKey).join("|") : channelKey(channels)
    ]
  );
  const enabled = viewOptions.enabled ?? true;
  const isLive = Boolean(client) && list2.length > 0 && enabled && Boolean(groupId);
  React10.useEffect(() => {
    if (!isLive || !client || !groupId) return;
    const onMessage = (message) => {
      if (!matchesGroup(message, groupId)) return;
      setLastEventAt(Date.now());
      setEventCount((n) => n + 1);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => refetchRef.current(), debounceMs);
    };
    const offs = list2.map(
      (ch) => typeof ch === "string" ? client.subscribe(ch, onMessage) : client.subscribeChannel(ch, onMessage)
    );
    return () => {
      for (const off of offs) off();
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [client, list2, isLive, groupId, debounceMs]);
  return { ...base, isLive, lastEventAt, eventCount };
}
var OWN_DESCRIPTION = "The copy resolved for you (your private row, else the tenant-shared one).";
function buildScopeChoices(otherScopes, activeScope, ownLabel = "Your view") {
  return buildScopeChoices$1(otherScopes, activeScope, {
    ownLabel,
    ownDescription: OWN_DESCRIPTION,
    scopeLabel: transactionScopeLabel,
    scopeDescription: transactionScopeDescription
  });
}
function TransactionScopeSwitcher({
  otherScopes,
  activeScope = null,
  onSelect,
  busy = false,
  className
}) {
  return /* @__PURE__ */ jsx(
    ScopeSwitcher,
    {
      otherScopes,
      activeScope,
      onSelect,
      busy,
      className,
      copy: {
        ownLabel: "Your view",
        ownDescription: OWN_DESCRIPTION,
        scopeLabel: transactionScopeLabel,
        scopeDescription: transactionScopeDescription,
        ariaLabel: "View this transaction at another scope"
      }
    }
  );
}
var log4 = createLogger("tx-table:hydration");
var TX_LARGE_ATTRIBUTES = [
  "input",
  "output",
  "data",
  "logs",
  "internal_transactions",
  "native_transfers",
  "erc20_transfers",
  "nft_transfers",
  "decoded_transaction",
  "decoded_call",
  // The protocol-shaped semantic tree (`CanonicalTransaction.semantic`) the
  // `@authe/tx-semantic` view renders. Offloaded like the other heavy forensic
  // attributes and hydrated on drill-down.
  "semantic"
];
function createCdnOffloadFetcher(config) {
  return async (placeholder) => {
    const url = cdnUrlForKey(placeholder.key, config);
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit"
    });
    if (!res.ok) throw new Error(`CDN offload fetch failed (${res.status}) for ${placeholder.attribute}`);
    return res.json();
  };
}
function hasOffloadedAttributes(row, attributes = TX_LARGE_ATTRIBUTES) {
  return attributes.some((attr) => isOffloadPlaceholder(row[attr]));
}
async function hydrateTxRow(row, fetcher, attributes = TX_LARGE_ATTRIBUTES) {
  if (!hasOffloadedAttributes(row, attributes)) return row;
  const attrSet = new Set(attributes);
  return rehydrateItem(row, (placeholder) => {
    if (!attrSet.has(placeholder.attribute)) return Promise.resolve(row[placeholder.attribute]);
    return fetcher(placeholder);
  });
}
function useHydratedTxRow(row, options) {
  const { fetcher, attributes = TX_LARGE_ATTRIBUTES, enabled = true } = options;
  const [hydrated, setHydrated] = React10.useState(row);
  const [isLoading, setIsLoading] = React10.useState(false);
  const [error, setError] = React10.useState(null);
  const [trigger, setTrigger] = React10.useState(0);
  if (useDepsChanged([row])) {
    setHydrated(row);
    setError(null);
  }
  React10.useEffect(() => {
    if (!enabled && trigger === 0) return;
    if (!hasOffloadedAttributes(row, attributes)) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    hydrateTxRow(row, fetcher, attributes).then((next) => {
      if (!cancelled) setHydrated(next);
    }).catch((err) => {
      if (cancelled) return;
      log4.warn("row hydration failed", { error: String(err) });
      setError(err);
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [row, fetcher, enabled, trigger]);
  const hydrate = React10.useCallback(() => setTrigger((n) => n + 1), []);
  return { row: hydrated, isLoading, error, hydrate };
}
function useLazyAttribute(value, fetcher) {
  const placeholder = isOffloadPlaceholder(value) ? value : null;
  const [resolved, setResolved] = React10.useState(placeholder ? void 0 : value);
  const [isLoading, setIsLoading] = React10.useState(false);
  const [error, setError] = React10.useState(null);
  const runRef = React10.useRef(0);
  React10.useEffect(() => {
    runRef.current += 1;
    setResolved(isOffloadPlaceholder(value) ? void 0 : value);
    setIsLoading(false);
    setError(null);
  }, [value]);
  const load = React10.useCallback(() => {
    if (!placeholder || resolved !== void 0 || isLoading) return;
    const runId = ++runRef.current;
    const isCurrent = () => runId === runRef.current;
    setIsLoading(true);
    setError(null);
    fetcher(placeholder).then((v) => {
      if (isCurrent()) setResolved(v);
    }).catch((err) => {
      if (!isCurrent()) return;
      log4.warn("attribute hydration failed", { attribute: placeholder.attribute, error: String(err) });
      setError(err);
    }).finally(() => {
      if (isCurrent()) setIsLoading(false);
    });
  }, [placeholder, resolved, isLoading, fetcher]);
  return { value: resolved, isLoading, error, load };
}
function createCachedCdnOffloadFetcher(config, cache) {
  const base = createCdnOffloadFetcher(config);
  return async (placeholder) => {
    const cached = await cache.getStatic(placeholder.key);
    if (cached !== void 0) return cached;
    const value = await base(placeholder);
    await cache.setStatic(placeholder.key, value);
    return value;
  };
}
function txRowEnvelopeHeader(row) {
  return {
    canonical_tx_id: row.canonical_tx_id,
    chain_id: row.chain_id,
    pk: null,
    sk: null,
    block_hash: row.block_hash ?? "",
    block_number: row.block_number ?? 0,
    block_timestamp: row.block_timestamp ?? 0,
    finality: typeof row.finality === "string" ? row.finality : "unknown",
    participant_summary: row.participant_summary ?? {},
    total_value_raw: row.total_value_raw ?? "0",
    amount_hidden: row.amount_hidden ?? false,
    counts: row.counts ?? {},
    s3_key: row.s3_key ?? null,
    s3_etag: row.s3_etag ?? null,
    body_bytes: row.body_bytes ?? 0,
    body_compression: row.body_compression ?? "none",
    // The lean list projection carries the manifest (catchall passthrough) so the
    // body loader knows which heavy slices to lazy-load + merge after the core.
    body_slices: normalizeBodySlices(row.body_slices),
    schema_version: row.schema_version ?? "1"
  };
}
function normalizeBodySlices(value) {
  if (!value || typeof value !== "object") return void 0;
  const out = {};
  for (const [slice, ref] of Object.entries(value)) {
    const o = ref && typeof ref === "object" ? ref : {};
    out[slice] = {
      bytes: typeof o.bytes === "number" ? o.bytes : 0,
      etag: typeof o.etag === "string" ? o.etag : null
    };
  }
  return Object.keys(out).length > 0 ? out : void 0;
}
function createTxBodyLoader(config) {
  return createEnvelopeBodyLoader(config);
}
function useEnvelopeBody(row, loader, options = {}) {
  const { enabled = false, mergeSlices = false } = options;
  const [body, setBody] = React10.useState(void 0);
  const [isLoading, setIsLoading] = React10.useState(false);
  const [error, setError] = React10.useState(null);
  const [pendingSlices, setPendingSlices] = React10.useState([]);
  const [failedSlices, setFailedSlices] = React10.useState([]);
  const [trigger, setTrigger] = React10.useState(enabled ? 1 : 0);
  const sliceSelector = Array.isArray(mergeSlices) ? [...mergeSlices].sort().join(",") : String(mergeSlices);
  if (useDepsChanged([row.canonical_tx_id, row.s3_etag, enabled])) {
    setBody(void 0);
    setError(null);
    setPendingSlices([]);
    setFailedSlices([]);
    setTrigger(enabled ? 1 : 0);
  }
  React10.useEffect(() => {
    if (trigger === 0) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setFailedSlices([]);
    const header = txRowEnvelopeHeader(row);
    const wantedSlices = () => {
      const manifest = header.body_slices ?? {};
      const names2 = Array.isArray(mergeSlices) ? mergeSlices : mergeSlices ? Object.keys(manifest) : [];
      return names2.filter((slice) => manifest[slice]);
    };
    void (async () => {
      try {
        const core = await loader.loadBody(header).get();
        if (cancelled) return;
        setBody(core);
        const slices = wantedSlices();
        setPendingSlices(slices);
        if (slices.length === 0) return;
        const working = cloneBody(core);
        await Promise.all(
          slices.map(async (slice) => {
            try {
              const payload = await loader.loadSlice(header, slice);
              if (cancelled || payload === void 0) return;
              mergeSlice(working, slice, payload);
              setBody({ ...working });
            } catch (err) {
              if (!cancelled) {
                log4.warn("envelope slice load failed", {
                  tx: row.canonical_tx_id,
                  slice,
                  error: String(err)
                });
                setFailedSlices((prev) => prev.includes(slice) ? prev : [...prev, slice]);
              }
            } finally {
              if (!cancelled) {
                setPendingSlices((prev) => prev.filter((s) => s !== slice));
              }
            }
          })
        );
      } catch (err) {
        if (cancelled) return;
        log4.warn("envelope body load failed", { tx: row.canonical_tx_id, error: String(err) });
        setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [row.canonical_tx_id, row.s3_etag, loader, trigger, sliceSelector]);
  const load = React10.useCallback(() => setTrigger((n) => n === 0 ? 1 : n + 1), []);
  return { body, isLoading, error, pendingSlices, failedSlices, load };
}

// src/graphqlFeed.ts
var FEED_UNSELECTED = ["total_value_raw", "type", "finality", "tx_hash", "from", "to"];
function feedRowToEnrichedTxRow(row) {
  const primaryEntity = row.entities[0];
  const base = coerceTransactionRow({
    canonical_tx_id: row.canonicalTxId,
    chain_id: row.chainId,
    block_timestamp: row.blockTimestamp,
    primary_entity_id: primaryEntity?.id ?? null
  });
  for (const key of FEED_UNSELECTED) delete base[key];
  const glyph = (ref) => ({
    logo: ref.logo,
    color: ref.color
  });
  const labels = row.labels.map((l) => ({ label: l.name ?? l.catalogId, ...glyph(l) }));
  const tags = row.tags.map((t) => ({ label: t.name ?? t.catalogId, ...glyph(t) }));
  const groups = row.groups.map((g) => ({ id: g.catalogId, name: g.name, ...glyph(g) }));
  const enrichment = {
    labels,
    tags,
    groups,
    entity: primaryEntity ? { id: primaryEntity.id, name: primaryEntity.name ?? primaryEntity.id } : null
  };
  return { ...base, enrichment };
}
function feedToEnrichedRows(feed) {
  if (!feed) return [];
  const items = Array.isArray(feed) ? feed : feed.items;
  return (items ?? []).map(feedRowToEnrichedTxRow);
}
function enrichFallbackRow(raw2) {
  const row = coerceTransactionRow(raw2);
  const r = row;
  const to = asString(r.to);
  const entityName = asString(r.to_entity_name) ?? asString(r.to_address_label);
  const enrichment = {
    from: asString(r.from),
    to,
    entity: entityName ? { id: to ?? entityName, name: entityName } : null
  };
  return { ...row, enrichment };
}
var EMPTY_ROWS = [];
function restItems(payload) {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.items) ? payload.items : [];
}
function useLatestTransactionsFeed(options = {}) {
  const limit = options.limit ?? 25;
  const active = useTransport("transactions.recentFeed") === "graphql";
  const query = useGraphqlQuery(
    "transactions.recentFeed",
    {
      graphql: async (client, signal) => feedToEnrichedRows(await fetchLatestTransactions(client, { limit }, { signal })),
      rest: async (client, signal) => {
        const payload = unwrapData(
          await client.get("/transactions", { query: { limit }, signal })
        );
        return restItems(payload).map(enrichFallbackRow);
      }
    },
    { enabled: (options.enabled ?? true) && active, deps: [limit] }
  );
  const rows = query.data ?? EMPTY_ROWS;
  return {
    rows,
    isLoading: query.isLoading && active,
    isFetching: query.isFetching,
    error: query.error,
    active,
    refetch: query.refetch
  };
}

export { EXPORT_ROW_CEILING, NATIVE_PROVEN_MIN_RAW, NO_TX_SERVER_FILTERS, TRANSACTIONS_TABLE_ID, TRANSACTION_DEBUG_RESOURCE, TX_COLUMN_LABELS, TX_EXPORT_COLUMNS, TX_FILTER_CODEC, TX_FILTER_OPS, TX_LARGE_ATTRIBUTES, TransactionExportMenu, TransactionFacets, TransactionLegend, TransactionScopeSwitcher, TransactionTable, activeTxServerFilters, addressFeedCounterparties, addressFeedPoisoning, addressFeedStanding, bodyDebugGroups, bodyTransfers, buildScopeChoices, catalogPoisonersOf, classifyTxRows, columnFiltersToFilterRules, createCachedCdnOffloadFetcher, createCdnOffloadFetcher, createTransactionColumns, createTxBodyLoader, feedRowToEnrichedTxRow, feedToEnrichedRows, filterRulesToColumnFilters, filterRulesToQuery, flaggedIds, getRowEnrichment, hasNoValueMovement, hasOffloadedAttributes, headlineProtocolId, hydrateTxRow, impersonationOf, isFieldNotLoaded, isSystemTxRow, matchesGroup, mergeTransactionRows, mergeTransactionRowsInFeedOrder, nativeProofMin, nativeValueProves, otherParty, priceQualityFacts, resolveRisk, rowBlockTimeMs, rowChainId, rowCounterfeitFlag, rowCounterfeitTokens, rowMatchesTxServerFilters, rowProtocols, subjectTransferLegs, summarizeTransactions, toTokenFlow, txBodyAirdrop, txBodyFlag, txBodySpoofing, txFeedTime, txRiskTitle, txRowEnvelopeHeader, txRowId, txRowKey, txRowRisk, txSelectionRowId, txServerFilterQuery, txStreamKey, useChainHeadReader, useChainHeads, useEnvelopeBody, useGroupView, useGroupViewLive, useHydratedTxRow, useLatestTransactionsFeed, useLazyAttribute, useLiveFinalityRows, useTransactionDebug, useTransactionDetail, useTransactionStream, useTransactionViews, useTransactionsQuery, withLiveFinality, withoutDustRows };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map